import React, { useState, useEffect, useRef } from 'react';
import { 
  Menu, X, Download, Copy, Check, Type, MousePointer2, Layout, 
  CreditCard, MessageSquare, ChevronRight, Terminal, CheckCircle2, 
  Star, Briefcase, MapPin, Smile, User, Settings, Calendar, Edit, 
  AlertCircle, HelpCircle, Info, ArrowLeft, Building2, BadgeCheck, 
  CloudUpload, Mail, Zap, FileText, CheckCircle, Loader2, Sparkles, 
  Clock, Share2, UserPlus, Link, Linkedin, ThumbsUp, Lock, IndianRupee, 
  Twitter, Github, Facebook, Instagram, FileUser, Scan, Users, BookOpen, 
  Brain, CircleX, AlertTriangle, List, LogOut, Target, MoreVertical, Home,
  ChevronLeft, ChevronsLeft, ChevronsRight, ChevronDown, LayoutGrid,
  RefreshCw, Replace, MoreHorizontal, Filter, ArrowUp, ArrowDown, Plus, Trash2,
  Bold, Italic, Underline, Link2, ListOrdered, Image as ImageIcon,
  BookMarked, Calculator, Eye, Layers, Monitor, Smartphone, Ruler, Palette
} from 'lucide-react';

// ==================================================================================
// 1. MERGED CSS CONTENT (The Single Source of Truth)
// ==================================================================================
const MERGED_STYLES = `
/*
 * ========================================================================
 * LYWO ENTERPRISE STYLE GUIDE - MAIN CSS (main.css)
 * ========================================================================
 * * INSTRUCTIONS FOR USE:
 * 1. Global Setup: Include this file in your project's main layout.
 * * 2. Layout Density Engine: By default, the layout uses "Comfortable" spacing. 
 * To seamlessly switch the entire application to "Compact" mode, simply add 
 * the data attribute to your root element:
 * <html data-density="compact"> or <body data-density="compact">
 * * 3. Dark Mode Support: The UI is driven by Semantic Tokens (e.g. var(--brand-bg)).
 * To implement dark mode, simply overwrite these semantic variables via JS
 * on the :root element. No class name changes required.
 * * 4. Dynamic Forms: Wrap vertical or horizontal form rows in <div class="form-flex-col"> 
 * to automatically inherit dynamic gap spacing from the Density Engine.
 * ========================================================================
 */

@charset "UTF-8";
@import url("https://cdn.jsdelivr.net/npm/bootstrap@5.3.3/dist/css/bootstrap.min.css");
/* 2025 Best Practice: Adopting Variable Fonts (Inter) for dynamic weight/width */
@import url("https://fonts.googleapis.com/css2?family=Inter:ital,opsz,wght@0,14..32,100..900;1,14..32,100..900&display=swap");
@import url("https://fonts.googleapis.com/css2?family=Roboto:wght@400;500;700&display=swap");

:root {
  /* ========================================================= */
  /* 1. PRIMITIVE TOKENS (Raw Visual Values)                   */
  /* Avoid using these directly in UI components. Use Semantic */
  /* tokens instead to ensure Dark Mode compatibility.         */
  /* ========================================================= */
  --color-blue-500: #3B7CF2; /* Updated Bright Blue */
  --color-blue-400: #5a91f5;
  --color-indigo-100: #eef2ff;
  --color-green-100: #d1e7dd;
  --color-green-700: #198754;
  --color-yellow-100: #fff3cd;
  --color-yellow-700: #856404;
  --color-red-100: #f8d7da;
  --color-red-700: #dc3545;

  --color-gray-50: #F3F5F9; /* Updated Light Gray/Off-White */
  --color-gray-100: #e5e7eb;
  --color-gray-200: #d0d5dd;
  --color-gray-700: #667085;
  --color-gray-800: #344054; 
  --color-gray-900: #1F222A; /* Updated Dark Gray/Black */
  
  --color-black: #000000;
  --color-black-88: #000000E0;

  /* ========================================================= */
  /* 2. SEMANTIC TOKENS (Contextual Roles for Theming)         */
  /* Apply these to components. Overwrite these in Dark Mode.  */
  /* ========================================================= */
  --brand-primary: var(--color-blue-500);
  --brand-accent: #081DA5; /* Updated Dark Blue Logo Color */
  --brand-bg: var(--color-gray-50);
  --brand-text: var(--color-gray-900);
  
  --surface-bg: #ffffff;
  --surface-bg-hover: var(--color-gray-50);
  --surface-bg-alpha: rgba(255, 255, 255, 0.85);

  --color-info-bg: var(--color-indigo-100);
  --color-success-bg: var(--color-green-100);
  --color-success-text: var(--color-green-700);
  --color-warning-bg: var(--color-yellow-100);
  --color-warning-text: var(--color-yellow-700);
  --color-danger-bg: var(--color-red-100);
  --color-danger-text: var(--color-red-700);
  
  --ui-gray-bg: var(--color-gray-100);
  --ui-border: var(--color-gray-200);
  --ui-text-muted: var(--color-gray-700);
  --ui-text-dark: var(--color-gray-800);

  /* --- Dimensions & Z-Index --- */
  --radius-md: 0.5rem;
  --radius-lg: 0.75rem;
  --modal-height: 650px;
  --ff-z-index-app-header: 100;
  --ff-z-index-dropdown: 1050;
  --ff-z-index-modal: 1100;
  --ff-z-index-toast: 1200;

  /* ========================================================= */
  /* 3. TYPOGRAPHY & DYNAMIC SPACING TOKENS                    */
  /* These variables natively power the Layout Density Engine. */
  /* ========================================================= */
  --type-ratio: 1.125;
  --font-size-base: 0.6875rem; /* ~11px Standard Body */
  --font-size-sm: calc(var(--font-size-base) / var(--type-ratio)); /* ~9.77px Small Text */
  --font-size-h4: calc(var(--font-size-base) * var(--type-ratio) * var(--type-ratio)); /* ~13.92px */
  --font-size-h3: clamp(1rem, 0.9rem + 0.5vw, 1.1875rem); /* ~16-19px */
  --font-size-h2: clamp(1.25rem, 1.1rem + 1vw, 1.5rem); /* ~20-24px */
  --font-size-h1: clamp(1.5rem, 1.2rem + 1.5vw, 1.9375rem); /* ~24-31px */

  --font-family-base: 'Inter', sans-serif;
  --font-family-report: "Calibri", Arial, sans-serif;
  --ff-font-family-inter-regular: "Inter-Regular", sans-serif;
  --ff-font-family-job: "Source Sans Pro", sans-serif;
  --ff-font-family-fallback: Arial, sans-serif;
  
  /* DEFAULT (COMFORTABLE) DENSITY */
  --line-height-heading: 1.35; 
  --line-height-body: 1.7;     
  --line-height-sm: 1.5;       
  --margin-base: 1.5rem;
  --padding-card: 1.5rem;
  --gap-bento: 1.5rem;
  
  /* Dynamic Input Scaling */
  --input-min-height: 38px;
  --input-padding-y: 0.5rem;
  --input-padding-x: 0.75rem;

  /* ========================================================= */
  /* 4. EXTENDED & LEGACY TOKENS (Consolidated)                */
  /* ========================================================= */
  --color-gray-600: #475467;
  --color-gray-400: #9ca3af;
  --color-peach-bg: #ffe6d5;
  --color-slate-600: #51607A;
  --color-rose-400: #FB7185;
  --color-green-600: #009746;
  --color-blue-deep: #0047FF;
  --color-dark-navy: #1e293b;
  --color-slate-500: #64748b;
  --color-slate-700: #334155;
  --color-slate-400: #475569;
  --color-blue-navy: #1e3a8a;
  --color-highlight-blue: #3b82f6;
  --color-indigo-500: #6366f1;
  --color-sidebar-border: #d7e2ff;
  --color-sidebar-accent: #081da5;
}

/* * COMPACT DENSITY OVERRIDES
 * Automatically shrinks margins, paddings, and line-heights universally
 * when [data-density="compact"] is applied to a parent element/body.
 */
[data-density="compact"] {
  --line-height-heading: 1.14; 
  --line-height-body: 1.4;     
  --margin-base: 0.75rem;
  --padding-card: 1rem;
  --gap-bento: 0.75rem;
}

html { 
  scroll-behavior: smooth; 
  font-size: 100%; 
}

body { 
  margin: 0; padding: 0; 
  font-family: var(--font-family-base) !important; 
  font-size: var(--font-size-base); 
  background-color: var(--brand-bg); 
  -webkit-font-smoothing: antialiased; 
  line-height: var(--line-height-body); 
  color: var(--brand-text);
  font-weight: 400;
}

/* 2025 Best Practice: Override Bootstrap classes for Dark Mode Compatibility */
.bg-white { background-color: var(--surface-bg) !important; }
.bg-light { background-color: var(--ui-gray-bg) !important; }
.text-dark { color: var(--brand-text) !important; }
.border { border-color: var(--ui-border) !important; }

/* Responsive Images */
img { max-width: 100%; height: auto; }

/* Global Utilities */
.text-truncate { white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }

/* * BENTO GRID LAYOUTS
 * Use this class on a container to instantly create a responsive grid 
 * that automatically adjusts its gap based on the active Layout Density.
 */
.bento-grid { display: grid; grid-template-columns: 1fr; gap: var(--gap-bento); align-items: stretch; transition: gap 0.3s ease; }
@media (min-width: 768px) { .bento-grid { grid-template-columns: repeat(2, 1fr); } }
@media (min-width: 1024px) { .bento-grid { grid-template-columns: repeat(3, 1fr); } }

/* * DYNAMIC FORM LAYOUTS
 * Wrap your standard form groups or horizontal rows in these classes.
 * They automatically inject the correct '--margin-base' gap between fields,
 * ensuring perfectly uniform spacing without hardcoding margin-bottoms.
 */
.form-flex-col { display: flex; flex-direction: column; gap: var(--margin-base); transition: gap 0.3s ease; }
.job-description-form { display: flex; flex-direction: column; gap: var(--margin-base); transition: gap 0.3s ease; }

/* ========================================= */
/* TYPOGRAPHY GLOBAL STYLES                  */
/* ========================================= */

h1, .h1 { font-size: var(--font-size-h1) !important; line-height: var(--line-height-heading); font-weight: 700; letter-spacing: -0.02em; margin-bottom: calc(var(--margin-base) * 0.5); color: var(--brand-text); transition: margin 0.3s ease; }
h2, .h2 { font-size: var(--font-size-h2) !important; line-height: var(--line-height-heading); font-weight: 700; letter-spacing: -0.01em; margin-bottom: calc(var(--margin-base) * 0.5); color: var(--brand-text); transition: margin 0.3s ease; }
h3, .h3 { font-size: var(--font-size-h3) !important; line-height: var(--line-height-heading); font-weight: 700; margin-bottom: calc(var(--margin-base) * 0.5); color: var(--brand-text); transition: margin 0.3s ease; }
h4, .h4 { font-size: var(--font-size-h4) !important; line-height: var(--line-height-heading); font-weight: 700; margin-bottom: calc(var(--margin-base) * 0.5); color: var(--brand-text); transition: margin 0.3s ease; }

.text-sm { font-size: var(--font-size-sm) !important; line-height: var(--line-height-sm); }
.text-base { font-size: var(--font-size-base) !important; line-height: var(--line-height-body); }

p { font-size: var(--font-size-base); line-height: var(--line-height-body); text-align: left; margin-bottom: 1rem; color: var(--brand-text); transition: margin 0.3s ease; }

.fw-bold { font-weight: 700 !important; }
.fw-normal { font-weight: 400 !important; }
.fw-medium { font-weight: 500 !important; }

/* Text Colors */
.text-primary { color: var(--brand-primary) !important; }
.text-danger { color: var(--color-danger-text) !important; }
.text-warning { color: var(--color-warning-text) !important; }
.text-info { color: var(--color-info-text) !important; }
.text-success { color: var(--color-success-text) !important; }
.text-muted { color: var(--ui-text-muted) !important; }
.text-center { text-align: center !important; }

.icon-baseline { margin-top: 4px; display: inline-block; line-height: 0; }

/* ========================================= */
/* LAYOUT & CORE COMPONENTS                  */
/* ========================================= */
.ds-wrapper { background-color: var(--brand-bg); min-height: 100vh; display: flex; flex-direction: column; min-width: 320px; transition: background-color 0.3s; }
.ds-wrapper.desktop-preview { min-width: 1024px; overflow-x: auto; }
.ds-header { background: var(--surface-bg); border-bottom: 1px solid var(--ui-border); position: sticky; top: 0; z-index: var(--ff-z-index-app-header); transition: background-color 0.3s; }
.ds-brand-row { display: flex; justify-content: space-between; align-items: center; padding: 0.75rem 1.5rem; border-bottom: 1px solid var(--ui-gray-bg); }
.ds-nav-row { padding: 0 1.5rem; overflow-x: auto; white-space: nowrap; background: var(--surface-bg); }
.ds-content { flex: 1; padding: 2rem; width: 100%; max-width: 1200px; margin: 0 auto; transition: background-color 0.3s; }

/* Buttons & Navigation */
.btn { font-weight: 600; padding: 0.375rem 1rem; border-radius: var(--radius-md); display: inline-flex; align-items: center; justify-content: center; gap: 0.5rem; transition: 0.2s; font-size: var(--font-size-base) !important; cursor: pointer; border: 1px solid transparent; }
.btn-primary { background-color: var(--brand-primary); border-color: var(--brand-primary); color: #fff; box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); }
.btn-primary:disabled { background-color: var(--ui-gray-bg); border-color: var(--ui-border); color: var(--ui-text-muted); cursor: not-allowed; }
.btn-outline-primary { color: var(--brand-primary); border: 1px solid var(--brand-primary) !important; background: var(--surface-bg); }
.btn-icon { padding: 8px; color: var(--ui-text-muted); border: 1px solid var(--ui-border); background: var(--surface-bg); }
.btn-ghost { background: none !important; border: none !important; cursor: pointer; padding: 0; line-height: 1; }
.btn-skip { background: transparent; border: none; color: var(--brand-primary); font-size: var(--font-size-base) !important; font-weight: 600; cursor: pointer; padding: 4px 8px; text-decoration: underline; text-decoration-thickness: 3px; text-underline-offset: 4px; text-decoration-color: rgba(59, 124, 242, 0.25); transition: all 0.2s ease; }
.text-action-link { background: transparent; border: none; padding: 0; color: var(--brand-primary); font-size: var(--font-size-base); font-weight: 500; cursor: pointer; transition: all 0.2s ease; display: inline-flex; align-items: center; text-decoration: none; }

@media (hover: hover) {
  .btn-primary:not(:disabled):hover { filter: brightness(0.9); }
  .btn-outline-primary:hover { background-color: var(--color-info-bg); }
  .btn-icon:hover { color: var(--brand-primary); border-color: var(--brand-primary); background: var(--color-info-bg); }
  .text-action-link:hover { text-decoration: underline; text-underline-offset: 4px; }
}

/* Tabs & Navigation Menus */
.nav-tabs-horizontal { display: flex; gap: 2rem; border-bottom: none; margin: 0; padding: 0; list-style: none; }
.nav-item-h { padding: 1rem 0.25rem; cursor: pointer; color: var(--ui-text-muted); font-weight: 500; font-size: var(--font-size-base); border-bottom: 2px solid transparent; transition: all 0.2s; display: flex; align-items: center; gap: 0.5rem; white-space: nowrap; flex-shrink: 0; }
.nav-item-h.active { color: var(--brand-primary); border-bottom-color: var(--brand-primary); }
@media (hover: hover) { .nav-item-h:hover { color: var(--brand-primary); } }

/* Context Menu */
.context-menu { background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-md); box-shadow: 0 4px 12px -2px rgba(0,0,0,0.1); min-width: 160px; padding: 4px 0; overflow: hidden; display: flex; flex-direction: column; height: max-content; }
.context-menu-item { display: flex; align-items: center; justify-content: flex-start; text-align: left; gap: 8px; padding: 0.375rem 0.75rem; font-size: var(--font-size-base); font-weight: 500; color: var(--brand-text); cursor: pointer; transition: 0.1s; white-space: nowrap; width: 100%; border: none; background: transparent; }
.context-menu-item svg { flex-shrink: 0; }
.context-menu-item.danger { color: var(--color-danger-text); }
@media (hover: hover) {
  .context-menu-item:hover { background-color: var(--ui-gray-bg); color: var(--brand-primary); }
  .context-menu-item.danger:hover { background-color: var(--color-danger-bg); }
}

/* Pagination */
.pagination-wrapper { display: flex; justify-content: flex-end; align-items: center; gap: var(--gap-bento); padding: var(--margin-base) 0; width: 100%; }
.pagination-nav { display: flex; align-items: center; gap: 0.5rem; }
.page-nav-link { display: inline-flex; align-items: center; justify-content: center; width: 28px; height: 28px; color: var(--ui-text-muted); cursor: pointer; background: transparent; border: 1px solid transparent; border-radius: 6px; font-size: var(--font-size-base); transition: all 0.2s; }
.page-nav-link.disabled { opacity: 0.4; cursor: not-allowed; pointer-events: none; }
@media (hover: hover) { .page-nav-link:hover { color: var(--brand-primary); background-color: var(--ui-gray-bg); } }
.page-number { font-weight: 500; font-size: var(--font-size-base); color: var(--brand-text); cursor: pointer; }
.page-select-wrapper { position: relative; display: flex; align-items: center; }
.page-select { appearance: none; padding: 0.375rem 24px 0.375rem 8px; border: 1px solid var(--ui-border); border-radius: 6px; background: var(--surface-bg); font-size: var(--font-size-base); font-weight: 500; color: var(--brand-text); cursor: pointer; min-width: 60px; line-height: 1.5; min-height: 32px; transition: all 0.3s ease; }
.page-select:focus { outline: none; border-color: var(--brand-primary); }
.page-select-icon { position: absolute; right: 6px; pointer-events: none; width: 12px; height: 12px; color: var(--ui-text-muted); }

/* Forms & Inputs - Maintained Strictly at Enterprise Sizing */
.form-control, .form-select { color: var(--brand-text) !important; font-size: var(--font-size-base) !important; font-weight: 400 !important; border-radius: 8px !important; min-height: 38px !important; border: 1px solid var(--ui-border) !important; padding: 0.375rem 0.75rem !important; background: var(--surface-bg); width: 100%; line-height: 1.5 !important; transition: all 0.3s ease; }
.form-control::placeholder, .form-select::placeholder, .selectPlaceholder::placeholder, .input-text-14::placeholder, .textarea-placeholder-14::placeholder { font-size: var(--font-size-base) !important; font-weight: 400 !important; color: var(--color-gray-400) !important; }
.form-control:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 4px rgba(59, 124, 242, 0.15); }
.form-label { font-size: var(--font-size-base) !important; font-weight: 700 !important; color: var(--brand-text); margin-bottom: 6px; display: block; }

/* Custom Controls */
.topic-chip { padding: 4px 12px; border-radius: 16px; font-size: var(--font-size-sm); font-weight: 500; background: var(--ui-gray-bg); color: var(--ui-text-dark); border: 1px solid var(--ui-border); cursor: pointer; transition: all 0.2s ease; }
.topic-chip.selected { background: var(--color-info-bg); color: var(--brand-primary); border-color: var(--brand-primary); }

.custom-switch-wrap { display: flex; align-items: center; cursor: pointer; user-select: none; gap: 8px; }
.switch-input { position: absolute; opacity: 0; width: 0; height: 0; }
.switch-slider { position: relative; display: inline-block; width: 36px; height: 20px; background-color: var(--ui-border); border-radius: 34px; transition: background-color 0.3s; }
.switch-slider::before { position: absolute; content: ""; height: 16px; width: 16px; left: 2px; bottom: 2px; background-color: #fff; transition: transform 0.3s; border-radius: 50%; box-shadow: 0 1px 2px rgba(0,0,0,0.2); }
.switch-input:checked + .switch-slider { background-color: var(--brand-primary); }
.switch-input:checked + .switch-slider::before { transform: translateX(16px); }

.custom-checkbox-wrap { display: flex; align-items: center; gap: 8px; cursor: pointer; user-select: none; }
.custom-checkbox { width: 18px; height: 18px; border-radius: 4px; border: 1px solid var(--ui-border); appearance: none; -webkit-appearance: none; outline: none; cursor: pointer; position: relative; background-color: var(--surface-bg); transition: all 0.2s; flex-shrink: 0; }
.custom-checkbox:checked { border-color: var(--brand-primary); background-color: var(--color-info-bg); }
.custom-checkbox:checked::after { content: "✓"; font-size: 12px; font-weight: bold; color: var(--brand-primary); position: absolute; top: 50%; left: 50%; transform: translate(-50%, -50%); }

.custom-radio { width: 18px; height: 18px; appearance: none; -webkit-appearance: none; border: 1px solid var(--ui-border); border-radius: 50%; outline: none; cursor: pointer; position: relative; background: var(--surface-bg); transition: all 0.2s; flex-shrink: 0;}
.custom-radio:checked { border-color: var(--brand-primary); border-width: 5px; }

/* Custom Range Slider */
.custom-range {
  -webkit-appearance: none;
  appearance: none;
  width: 100%;
  height: 6px;
  background: var(--ui-border);
  border-radius: 4px;
  outline: none;
  transition: background 0.3s;
}
.custom-range::-webkit-slider-thumb {
  -webkit-appearance: none;
  appearance: none;
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--brand-primary);
  cursor: pointer;
  border: 2px solid var(--surface-bg);
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: transform 0.1s;
}
.custom-range::-webkit-slider-thumb:hover {
  transform: scale(1.15);
}
.custom-range::-moz-range-thumb {
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: var(--brand-primary);
  cursor: pointer;
  border: 2px solid var(--surface-bg);
  box-shadow: 0 1px 3px rgba(0,0,0,0.3);
  transition: transform 0.1s;
}
.custom-range::-moz-range-thumb:hover {
  transform: scale(1.15);
}

/*
 * TOUCH TARGET EXPANSION
 * Ensures actionable items remain easily tappable on mobile devices
 * by forcing a minimum height of 44px (Apple Accessibility Standard).
 */
@media (pointer: coarse) {
  .btn, .form-control, .form-select, .page-nav-link, .nav-item-h, .context-menu-item, .btn-skip, .text-action-link, .custom-switch-wrap, .custom-checkbox-wrap, .btn-tag-toggle, .tag-label-item {
    min-height: 44px !important;
  }
}

/* ========================================= */
/* MODULES & OVERRIDES                       */
/* ========================================= */

/* * ANT DESIGN OVERRIDES
 * Strips hardcoded Ant Design padding and injects our dynamic layout tokens
 * so tables adapt to Compact/Comfortable modes seamlessly.
 */
.ant-table-wrapper .ant-table-cell,
.ant-table-wrapper .ant-table-thead > tr > th,
.ant-table-wrapper .ant-table-tbody > tr > th,
.ant-table-wrapper .ant-table-tbody > tr > td,
.ant-table-wrapper tfoot > tr > th,
.ant-table-wrapper tfoot > tr > td,
:where([class^="ant-table"]).ant-table-wrapper .ant-table-cell {
  padding: 12px 16px !important;
  font-size: var(--font-size-base) !important;
  line-height: 1.5 !important;
  transition: all 0.3s ease;
  background: transparent !important;
  color: var(--brand-text) !important;
  border-bottom: 1px solid var(--ui-border) !important;
  white-space: nowrap !important;
}
.ant-table-wrapper .ant-table {
  font-size: var(--font-size-base) !important;
  font-family: var(--font-family-base) !important;
  background: transparent !important;
}
.ant-table-tbody-virtual-scrollbar-thumb {
  margin-top: 7px !important;
  visibility: visible !important;
  border-radius: 0px !important;
  background: #c1c1c1 !important;
}
.ant-table-content thead tr {
  position: sticky;
  top: 0px;
  background-color: var(--ui-gray-bg) !important;
  z-index: 10;
}
.ant-table-content thead tr th {
  background-color: var(--ui-gray-bg) !important;
  font-weight: 600 !important;
}
.virtual-row td {
  height: 48px;
  line-height: 48px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.ant-pagination { display: none !important; }
.overFlowTable .ant-table-container { border: none !important; }
@media (hover: hover) { .ant-table tbody tr:hover td { background-color: var(--surface-bg-hover) !important; cursor: default;} }
.ant-header-content { display: flex; align-items: center; justify-content: space-between; gap: 8px; white-space: nowrap; }

/* Custom Modals */
.modal-h-650 { height: var(--modal-height) !important; }
.modal-h-650 .custom-modal-body { overflow-y: auto; }
.custom-modal-overlay { position: fixed; inset: 0; background-color: rgba(0,0,0,0.5); backdrop-filter: blur(4px); -webkit-backdrop-filter: blur(4px); z-index: var(--ff-z-index-modal); display: flex; align-items: center; justify-content: center; animation: fadeIn 0.2s ease-out; }
.custom-modal-dialog { background: var(--surface-bg); width: 100%; max-width: 500px; max-height: 90vh; border-radius: var(--radius-lg); box-shadow: 0 10px 25px rgba(0,0,0,0.2); display: flex; flex-direction: column; overflow: hidden; animation: slideUp 0.3s ease-out; }
.custom-modal-header { padding: 1rem 1.5rem; border-bottom: 1px solid var(--ui-border); display: flex; justify-content: space-between; align-items: center; background: var(--surface-bg); }
.custom-modal-body { padding: 1.5rem; flex: 1; overflow-y: auto; background: var(--surface-bg); scrollbar-width: thin; scrollbar-color: var(--ui-border) var(--surface-bg); }
.custom-modal-footer { padding: 1rem 1.5rem; border-top: 1px solid var(--ui-border); background: var(--surface-bg-hover); display: flex; justify-content: flex-end; gap: 1rem; }

/* Split Dropdown Fixes */
.add-q-reorder-btn { display: flex; align-items: center; gap: 6px; padding: 0.375rem 0.75rem; font-size: var(--font-size-base); font-weight: 500; border: 1px solid var(--ui-border); border-radius: 8px; background: var(--surface-bg); color: var(--ui-text-dark); cursor: pointer; white-space: nowrap; transition: all 0.3s ease; }
.add-q-reorder-btn:hover { background: var(--ui-gray-bg); }
.add-q-split-wrapper { position: relative; display: inline-block; z-index: 10; }
.add-q-split-btn { display: inline-flex; align-items: stretch; padding: 0 !important; overflow: hidden; border-radius: 8px !important; background-color: var(--brand-primary); box-shadow: 0 1px 2px rgba(16, 24, 40, 0.05); transition: all 0.3s ease; }
.add-q-split-main-btn { background: transparent; color: #fff; border: none; padding: 0.375rem 1rem; font-weight: 600; font-size: var(--font-size-base); cursor: pointer; display: flex; align-items: center; transition: all 0.2s ease; }
.add-q-split-main-btn:hover { background: rgba(0, 0, 0, 0.1); }
.add-q-chevron-btn { display: flex; align-items: center; justify-content: center; padding: 0 0.75rem; background: rgba(0, 0, 0, 0.15); border: none; border-left: 1px solid rgba(255, 255, 255, 0.25); color: #fff; cursor: pointer; transition: all 0.2s ease; }
.add-q-chevron-btn:hover { background: rgba(0, 0, 0, 0.25); }
.add-q-menu { position: absolute; right: 0; top: calc(100% + 4px); background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: 8px; box-shadow: 0px 8px 16px -4px rgba(16, 24, 40, 0.08); z-index: var(--ff-z-index-dropdown); min-width: 180px; padding: 4px; display: flex; flex-direction: column; gap: 2px; }
.add-q-menu-item { display: flex; align-items: center; gap: 8px; width: 100%; padding: 0.5rem 0.75rem; font-size: var(--font-size-base); font-weight: 500; color: var(--ui-text-dark); background: none; border: none; border-radius: 6px; cursor: pointer; text-align: left; transition: all 0.2s ease; }
.add-q-menu-item:hover { background: var(--ui-gray-bg); color: var(--brand-primary); }
.add-q-menu-item svg { color: var(--ui-text-muted); transition: color 0.2s ease; }
.add-q-menu-item:hover svg { color: var(--brand-primary); }
.points-dropdown-wrapper { display: flex; align-items: center; gap: 8px; flex-shrink: 0; }
.points-select { border: 1px solid var(--brand-primary); border-radius: 4px; padding: 2px 6px; background: var(--surface-bg); font-weight: 700; font-size: var(--font-size-base); color: var(--brand-primary); outline: none; cursor: pointer; }

/* Form Component Fixes */

/* Tag Selector Component */
.tag-selector-wrap { position: relative; width: 100%; }
.btn-tag-toggle { display: flex; align-items: center; justify-content: space-between; width: 100%; padding: 8px 12px; background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-md); box-shadow: 0 1px 2px rgba(0,0,0,0.05); cursor: pointer; transition: all 0.3s ease; }
.btn-tag-toggle:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0 0 0 2px rgba(59, 124, 242, 0.15); }
.btn-tag-toggle:hover { background: var(--surface-bg-hover); }
.tag-dropdown-menu { position: absolute; top: calc(100% + 4px); left: 0; right: 0; background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-md); box-shadow: 0 4px 12px rgba(0,0,0,0.1); z-index: var(--ff-z-index-dropdown); padding: 8px; }
.tag-list { max-height: 180px; overflow-y: auto; margin-bottom: 8px; scrollbar-width: thin; }
.tag-label-item { display: flex; align-items: center; gap: 8px; padding: 6px 8px; cursor: pointer; border-radius: 4px; margin: 0; }
.tag-label-item:hover { background-color: var(--ui-gray-bg); }
.tag-checkbox { margin: 0; cursor: pointer; }
.tag-text { font-size: var(--font-size-base); color: var(--brand-text); }
.tag-add-section { display: flex; gap: 8px; margin-bottom: 8px; position: relative; }
.tag-add-input { width: 100%; padding: 6px 32px 6px 8px; font-size: var(--font-size-sm); line-height: 1.25; border: 1px solid var(--ui-border); border-radius: 4px; outline: none; background: var(--surface-bg); color: var(--brand-text); transition: all 0.3s ease; }
.tag-add-input:focus { border-color: var(--brand-primary); box-shadow: 0 0 0 1px var(--brand-primary); }
.tag-add-btn { position: absolute; right: 4px; top: 50%; transform: translateY(-50%); background: var(--ui-gray-bg); color: var(--ui-text-dark); border: none; border-radius: 4px; width: 24px; height: 24px; display: flex; align-items: center; justify-content: center; cursor: pointer; font-weight: bold; line-height: 1; }
.tag-add-btn:hover { background: var(--ui-border); }
.tag-footer { display: flex; justify-content: flex-end; gap: 8px; border-top: 1px solid var(--ui-border); padding-top: 8px; margin-top: 8px; }
.btn-tag-reset { background: transparent; border: none; color: var(--ui-text-muted); cursor: pointer; font-size: var(--font-size-sm); font-weight: 500; }
.btn-tag-apply { background: var(--brand-primary); color: white; border: none; border-radius: 4px; padding: 4px 12px; cursor: pointer; font-size: var(--font-size-sm); font-weight: 500; transition: background-color 0.2s; }
.btn-tag-apply:disabled { background: var(--ui-gray-bg); color: var(--ui-text-muted); cursor: not-allowed; }

/* Rich Text Editor Component */
.rich-text-editor { border: 1px solid var(--ui-border); border-radius: var(--radius-md); background: var(--surface-bg); display: flex; flex-direction: column; overflow: hidden; width: 100%; }
.rte-toolbar { display: flex; align-items: center; gap: 8px; padding: 8px 12px; border-bottom: 1px solid var(--ui-border); background: var(--surface-bg-hover); flex-wrap: wrap; }
.rte-btn { cursor: pointer; color: var(--ui-text-muted); padding: 4px; border-radius: 4px; transition: all 0.2s ease; display: flex; align-items: center; justify-content: center; }
.rte-btn:hover { background: var(--ui-gray-bg); color: var(--brand-primary); }
.rte-content { padding: 12px 16px; min-height: 120px; color: var(--brand-text); font-size: var(--font-size-base); line-height: 1.5; outline: none; background: var(--surface-bg); }
.rte-content:focus { background: var(--surface-bg); }

.skillGroup { padding: 8px; border-radius: var(--radius-skill); font-size: var(--font-size-base); color: var(--ff-color-skill-text); background: var(--ff-color-groupbar-bg); cursor: pointer; position: relative; white-space: nowrap; transition: all 0.3s ease; }

/* Admin Login Section */
.clWrap { display: flex; min-height: 100vh; }
.clLft-side { width: 340px; background-color: #f9fafb; padding: 40px 60px 40px 60px; }
.clLogoStyle { width: 100px; height: auto; object-fit: contain; position: fixed; }
.illustrationContainer { bottom: 50px; position: fixed; }
.formSpc { flex-grow: 1; padding: 80px 120px; display: flex; align-items: center; justify-content: center; }
.loginCode { width: 360px; }
.loginCode p { font-size: 27px; font-weight: 700; line-height: 38px; color: var(--brand-text); text-align: center; }
.loginCode b { color: var(--brand-primary); }
.loginCode label { display: block; margin-bottom: 5px; font-size: 14px; font-weight: 500; color: var(--ui-text-dark); }
.loginCode .formArea input[type="text"] { width: 100%; padding: 8px 12px 8px 33px; border: 1px solid var(--ui-border); border-radius: 8px !important; box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05); font-size: var(--font-size-base); color: var(--brand-text); transition: all 0.3s ease; background: var(--surface-bg); }
.loginCode .formArea input[type="text"].error { border: 1px solid var(--color-danger-text); box-shadow: 0px 0px 0px 4px var(--color-danger-bg), 0px 1px 2px 0px rgba(16, 24, 40, 0.05); }
.loginCode .formArea input[type="text"]:focus { outline: none; border-color: var(--brand-primary); box-shadow: 0px 0px 0px 4px var(--color-info-bg), 0px 1px 2px 0px rgba(16, 24, 40, 0.05); }
@media (max-width: 640px) { .clLft-side { display: none; } .formSpc { padding: 24px 16px; } }

/* * CARDS & CONTAINERS
 * Standardized wrappers for grouping content dynamically.
 */
.card-std { background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-lg); padding: var(--padding-card); transition: all 0.3s ease; position: relative; display: flex; flex-direction: column; margin-bottom: var(--margin-base); color: var(--brand-text); }
.card-interactive { cursor: pointer; transition: all 0.2s; border: 1px solid transparent; }
@media (hover: hover) { .card-interactive:hover { border-color: var(--brand-primary) !important; background-color: var(--color-info-bg); transform: translateY(-2px); } }
.card-interactive.selected { border-color: var(--brand-primary) !important; background-color: var(--color-info-bg); }
.card-select { background: var(--surface-bg); border: 1px solid var(--ui-border); border-radius: var(--radius-lg); padding: var(--padding-card); cursor: pointer; transition: all 0.3s ease; position: relative; display: flex; flex-direction: column; justify-content: center; align-items: center; }
@media (hover: hover) { .card-select:hover { border-color: var(--brand-primary); transform: translateY(-2px); } }
.card-select.selected { border-color: var(--brand-primary); background-color: var(--color-info-bg); box-shadow: 0 0 0 1px var(--brand-primary); }
.card-select-header { text-align: center; margin-bottom: 8px; color: var(--brand-text); }
.card-pricing { border: 1px solid var(--ui-border); border-radius: var(--radius-lg); padding: var(--padding-card); background: var(--surface-bg); transition: all 0.3s ease; display: flex; flex-direction: column; justify-content: space-between; height: 100%; position: relative; }
@media (hover: hover) { .card-pricing:hover { transform: translateY(-4px); box-shadow: 0 12px 24px rgba(0,0,0,0.1); } }
.card-pricing.standard { border-color: var(--brand-primary); background-color: var(--color-info-bg); }
.card-pricing.premium { border-color: var(--color-success-text); background-color: var(--color-success-bg); }
.badge-floating { position: absolute; top: 0; left: 50%; transform: translate(-50%, -50%); background-color: var(--color-success-text); color: #fff; font-size: var(--font-size-sm); font-weight: 600; padding: 4px 12px; border-radius: 20px; white-space: nowrap; box-shadow: 0 2px 4px rgba(0,0,0,0.1); }

/* Badges (Mathematically Scaled - Small Text) */
.badge { display: inline-flex; align-items: center; padding: 0.25em 0.6em; border-radius: 9999px; font-size: var(--font-size-sm); font-weight: 600; line-height: 1; border: 1px solid transparent; white-space: nowrap; }
.badge-excellent { background-color: var(--color-success-bg); color: var(--color-success-text); border-color: var(--color-success-bg); }
.badge-good { background-color: var(--color-info-bg); color: var(--brand-primary); border-color: var(--color-info-bg); }
.badge-normal { background-color: var(--color-warning-bg); color: var(--color-warning-text); border-color: var(--color-warning-bg); }
.badge-poor { background-color: var(--color-danger-bg); color: var(--color-danger-text); border-color: var(--color-danger-bg); }

/* Layout Containers */
.content-area { background: var(--surface-bg); border-radius: var(--radius-lg); border: 1px solid var(--ui-border); box-shadow: 0px 4px 16px rgba(0, 0, 0, 0.04), 0px 1px 3px rgba(0, 0, 0, 0.02); overflow: hidden; height: 100%; display: flex; flex-direction: column; }
.panel-header { border-bottom: 1px solid var(--ui-border); background: var(--surface-bg); padding: var(--padding-card); display: flex; justify-content: space-between; align-items: center; transition: padding 0.3s ease; }
.assessment-table { background: var(--surface-bg); border-bottom: 1px solid var(--ui-border); flex: 1; overflow-x: auto; overflow-y: auto; scrollbar-width: thin; scrollbar-color: var(--ui-border) var(--surface-bg); }

/* VWO Cards */
.VWOcard { position: relative; width: 100%; max-width: 200px; min-height: 160px; background-color: var(--surface-bg); border-radius: var(--radius-md); padding: 16px 0; box-shadow: 0px 4px 6px 2px rgba(16,24,40,0.03); display: flex; align-items: center; justify-content: center; flex-direction: column; gap: 12px; border: 1px solid transparent; transition: all 0.2s; cursor: pointer;}
.VWOcard:hover { border-color: var(--brand-primary); }
.orngBrdr { border: 2px solid var(--color-danger-text); }
.vwo-icon-wrapper { width: 48px; height: 48px; border-radius: 50%; display: flex; align-items: center; justify-content: center; background-color: var(--color-info-bg); color: var(--brand-primary); }
.vwo-icon-wrapper.danger { background-color: var(--color-danger-bg); color: var(--color-danger-text); }

@keyframes slideInRight { from { transform: translateX(100%); opacity: 0; } to { transform: translateX(0); opacity: 1; } }
@keyframes slideUp { from { transform: translateY(20px); opacity: 0; } to { transform: translateY(0); opacity: 1; } }
@keyframes fadeIn { from { opacity: 0; transform: translateY(10px); } to { opacity: 1; transform: translateY(0); } }
`;

// ==================================================================================
// 2. CODE SNIPPETS
// ==================================================================================
const CODE_SNIPPETS = {
  primaryActions: `
<button class="btn btn-primary">Primary</button>
<button class="btn btn-primary" disabled>Inactive</button>
<button class="btn btn-outline-primary">Secondary</button>`,

  skipLink: `
<button class="btn-skip">Skip</button>`,

  labeledToggle: `
<div class="d-flex align-items-center gap-3">
  <span class="text-sm text-muted fw-medium">Inactive Only</span>
  <label class="custom-switch-wrap m-0">
    <div class="position-relative d-flex align-items-center">
      <input type="checkbox" class="switch-input" checked />
      <span class="switch-slider"></span>
    </div>
  </label>
  <span class="text-sm text-dark fw-medium">Active Only</span>
</div>`,

  textLinks: `
<button class="text-action-link">Edit</button>
<div class="d-flex gap-4">
  <button class="text-action-link">Advanced Filter</button>
  <button class="text-action-link text-decoration-underline">Clear All</button>
</div>`,

  splitButton: `
<div class="add-q-split-wrapper">
  <div class="add-q-split-btn">
    <button type="button" class="add-q-split-main-btn">Add/Edit Question</button>
    <button type="button" class="add-q-chevron-btn">
      <i class="icon-chevron-down"></i>
    </button>
  </div>
  <div class="add-q-menu">
    <button type="button" class="add-q-menu-item">
      <i class="icon-edit"></i> Manually
    </button>
    <button type="button" class="add-q-menu-item">
      <i class="icon-sparkles"></i> Generate with AI
    </button>
  </div>
</div>`,

  contextMenu: `<div class="context-menu">
  <button class="context-menu-item">
    <i class="icon-edit"></i> Edit
  </button>
  <button class="context-menu-item">
    <i class="icon-copy"></i> More Like This
  </button>
  <button class="context-menu-item">
    <i class="icon-replace"></i> Replace
  </button>
  <button class="context-menu-item danger">
    <i class="icon-trash"></i> Delete
  </button>
</div>`,

  tabs: `<div class="border-bottom w-100">
  <ul class="nav-tabs-horizontal mb-0">
    <li class="nav-item-h active">Dashboard</li>
    <li class="nav-item-h">Profile</li>
  </ul>
</div>`,

  pagination: `<div class="pagination-wrapper">
  <div class="pagination-nav">
    <button class="page-nav-link disabled"><i class="chevrons-left"></i></button>
    <button class="page-nav-link disabled"><i class="chevron-left"></i></button>
    <span class="page-number">2</span>
    <button class="page-nav-link"><i class="chevron-right"></i></button>
    <button class="page-nav-link"><i class="chevrons-right"></i></button>
  </div>
  <div class="page-select-wrapper">
    <select class="page-select">
      <option>500</option>
    </select>
    <i class="page-select-icon"></i>
  </div>
</div>`,

  docTitle: `<div class="w-100">
  <label class="form-label">Document Name</label>
  <input type="text" class="input-title-large" value="Q3 Financial Report" />
</div>`,

  topicSelector: `<div class="w-100">
  <h4 class="fw-bold mb-1">1. Set Context</h4>
  <p class="text-sm fw-bold text-muted mb-3">Select main topics (max 5)</p>
  
  <div class="position-relative mb-3">
    <input type="text" class="form-control input-action-pr" placeholder="Add custom topic..." style="padding-right: 40px;" />
    <button class="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent text-primary fw-bold d-flex align-items-center justify-content-center" style="width: 40px; height: 100%; font-size: 20px;">+</button>
  </div>
  
  <div class="d-flex flex-wrap gap-2">
    <div class="topic-chip">first topic</div>
    <div class="topic-chip selected">second topic</div>
    <div class="topic-chip">third topic</div>
    <div class="topic-chip selected">fourth topic</div>
    <div class="topic-chip">fifth topic</div>
  </div>
</div>`,

  tagSelector: `
<div class="tag-selector-wrap">
  <button type="button" class="btn-tag-toggle">
    <span class="text-sm text-muted">Set Tags</span>
    <i class="icon-chevron-down"></i>
  </button>

  <div class="tag-dropdown-menu">
    <div class="tag-list">
      <div class="d-flex justify-content-between align-items-center">
        <label class="tag-label-item flex-grow-1 m-0 mb-1">
          <input type="checkbox" class="custom-checkbox" checked />
          <span class="tag-text">In Progress</span>
        </label>
        <button type="button" class="text-action-link ms-4 me-2 flex-shrink-0 text-decoration-underline" style="font-size: var(--font-size-xs)">Clear All</button>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <label class="tag-label-item flex-grow-1 m-0 mb-1">
          <input type="checkbox" class="custom-checkbox" />
          <span class="tag-text">Call to Interview</span>
        </label>
      </div>
    </div>

    <div class="tag-add-section">
      <input type="text" class="tag-add-input" placeholder="Add Custom tag" />
      <button type="button" class="tag-add-btn" aria-label="Add custom tag">+</button>
    </div>

    <div class="tag-footer">
      <button type="button" class="btn-tag-reset">Reset</button>
      <button type="button" class="btn-tag-apply" disabled>Apply</button>
    </div>
  </div>
</div>`,

  inputs: `<div class="form-flex-col">
  <div>
    <label class="form-label">Email Address</label>
    <input type="email" class="form-control" placeholder="name@example.com" />
  </div>
  <div>
    <label class="form-label">Password</label>
    <input type="password" class="form-control" placeholder="••••••••" />
  </div>
</div>`,

  customControls: `
<label class="custom-switch-wrap mb-3">
  <span class="checkbox-label">Notifications</span>
  <div class="position-relative">
    <input type="checkbox" class="switch-input" checked />
    <span class="switch-slider"></span>
  </div>
</label>

<label class="custom-checkbox-wrap mb-2">
  <input type="checkbox" class="custom-checkbox" checked />
  <span class="checkbox-label">Remember me</span>
</label>

<label class="custom-checkbox-wrap">
  <input type="radio" name="options" class="custom-radio" checked />
  <span class="checkbox-label">Option A</span>
</label>`,

  otp: `<div class="d-flex justify-content-center gap-2">
  <input class="otp-input" value="4" />
  <input class="otp-input" value="2" />
  <input class="otp-input" />
  <input class="otp-input" />
</div>`,

  rte: `<div class="rich-text-editor">
  <div class="rte-toolbar">
    <i class="icon-bold rte-btn"></i>
    <i class="icon-italic rte-btn"></i>
    <i class="icon-underline rte-btn"></i>
    <span class="border-end mx-1"></span>
    <i class="icon-list rte-btn"></i>
    <i class="icon-list-ordered rte-btn"></i>
    <span class="border-end mx-1"></span>
    <i class="icon-link rte-btn"></i>
    <i class="icon-image rte-btn"></i>
  </div>
  <div class="rte-content" contenteditable="true">
    Start typing your content here...
  </div>
</div>`,

  standardCard: `<div class="card-std">
  <h4 class="fw-bold">Card Title</h4>
  <p class="text-muted m-0">Standard card container content.</p>
</div>`,

  pageContainer: `
<div class="content-area" style="height: 400px;">
  <div class="panel-header">
    <h3 class="fw-bold m-0">Group List</h3>
    
    <div class="d-flex align-items-center" style="gap: var(--margin-base);">
      <div class="d-flex align-items-center gap-2">
        <span class="text-sm text-muted fw-medium">Inactive Only</span>
        <label class="custom-switch-wrap m-0">
          <div class="position-relative d-flex align-items-center">
            <input type="checkbox" class="switch-input" checked />
            <span class="switch-slider"></span>
          </div>
        </label>
        <span class="text-sm text-dark fw-medium">Active Only</span>
      </div>
      
      <button class="btn btn-primary">Create Assessment Link</button>
    </div>
  </div>

  <div class="assessment-table p-0">
     <table class="ant-table">
        <thead>
          <tr>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Access Code</span>
                <div class="d-flex align-items-center gap-1">
                  <div class="d-flex flex-column align-items-center" style="line-height: 0.5; opacity: 0.5;">
                    <i class="icon-chevron-up" style="font-size: 10px;"></i>
                    <i class="icon-chevron-down" style="font-size: 10px;"></i>
                  </div>
                  <i class="icon-filter text-muted"></i>
                </div>
              </div>
            </th>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Posted Date</span>
                <div class="d-flex align-items-center gap-1">
                  <div class="d-flex flex-column align-items-center" style="line-height: 0.5; opacity: 0.5;">
                    <i class="icon-chevron-up" style="font-size: 10px;"></i>
                    <i class="icon-chevron-down" style="font-size: 10px;"></i>
                  </div>
                  <i class="icon-filter text-muted"></i>
                </div>
              </div>
            </th>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Form Type</span>
                <div class="d-flex align-items-center gap-1">
                  <div class="d-flex flex-column align-items-center" style="line-height: 0.5; opacity: 0.5;">
                    <i class="icon-chevron-up" style="font-size: 10px;"></i>
                    <i class="icon-chevron-down" style="font-size: 10px;"></i>
                  </div>
                  <i class="icon-filter text-muted"></i>
                </div>
              </div>
            </th>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Company</span>
                <div class="d-flex align-items-center gap-1">
                  <div class="d-flex flex-column align-items-center" style="line-height: 0.5; opacity: 0.5;">
                    <i class="icon-chevron-up" style="font-size: 10px;"></i>
                    <i class="icon-chevron-down" style="font-size: 10px;"></i>
                  </div>
                  <i class="icon-filter text-muted"></i>
                </div>
              </div>
            </th>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Designation</span>
                <div class="d-flex align-items-center gap-1">
                  <div class="d-flex flex-column align-items-center" style="line-height: 0.5; opacity: 0.5;">
                    <i class="icon-chevron-up" style="font-size: 10px;"></i>
                    <i class="icon-chevron-down" style="font-size: 10px;"></i>
                  </div>
                  <i class="icon-filter text-muted"></i>
                </div>
              </div>
            </th>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Applications</span>
                <div class="d-flex align-items-center gap-1">
                  <div class="d-flex flex-column align-items-center" style="line-height: 0.5; opacity: 0.5;">
                    <i class="icon-chevron-up" style="font-size: 10px;"></i>
                    <i class="icon-chevron-down" style="font-size: 10px;"></i>
                  </div>
                </div>
              </div>
            </th>
            <th>
              <div class="d-flex align-items-center gap-2 text-nowrap">
                <span>Actions</span>
              </div>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td>SYT0051</td>
            <td>25-03-26 10:44 PM</td>
            <td>Job Application</td>
            <td>Synnat Pharma</td>
            <td>Chemist - Production</td>
            <td>0 / 0</td>
            <td><button class="text-action-link"><i class="icon-more-vertical text-muted"></i></button></td>
          </tr>
          <tr>
            <td>SHU0001</td>
            <td>24-03-26 03:25 PM</td>
            <td>Job Application</td>
            <td>Shuban Software</td>
            <td>Test</td>
            <td>1 / 1</td>
            <td><button class="text-action-link"><i class="icon-more-vertical text-muted"></i></button></td>
          </tr>
          <tr>
            <td>SOL0020</td>
            <td>20-03-26 04:21 PM</td>
            <td>Job Application</td>
            <td>Solon India</td>
            <td>Sales Representatives - Field Sales</td>
            <td>0 / 0</td>
            <td><button class="text-action-link"><i class="icon-more-vertical text-muted"></i></button></td>
          </tr>
          <tr>
            <td>SOL0019</td>
            <td>20-03-26 12:36 PM</td>
            <td>Job Application</td>
            <td>Solon India</td>
            <td>Assitant Manager - Sales Support</td>
            <td>0 / 0</td>
            <td><button class="text-action-link"><i class="icon-more-vertical text-muted"></i></button></td>
          </tr>
          <tr>
            <td>SYT0049</td>
            <td>18-03-26 03:08 PM</td>
            <td>Job Application</td>
            <td>Synnat Pharma</td>
            <td>AR&D Manager</td>
            <td>0 / 0</td>
            <td><button class="text-action-link"><i class="icon-more-vertical text-muted"></i></button></td>
          </tr>
        </tbody>
     </table>
  </div>
</div>`,

  badges: `
<span class="badge badge-excellent">Excellent</span>
<span class="badge badge-good">Good</span>
<span class="badge badge-normal">Normal</span>
<span class="badge badge-poor">Poor</span>`,

  selectionCard: `<div class="d-flex flex-column gap-3 w-100">
  <div class="card-select w-100 selected position-relative h-100">
    <div class="card-select-header"><h3 class="fw-bold">Plan Pro</h3></div>
    <p class="text-center text-muted m-0">₹899/mo</p>
    <div class="position-absolute top-0 end-0 p-2 text-primary"><CheckCircle2 size={18} /></div>
  </div>
  <div class="card-select w-100 h-100">
    <div class="card-select-header"><h3 class="fw-bold">Enterprise</h3></div>
    <p class="text-center text-muted m-0">Contact Us</p>
  </div>
</div>`,

  roleCard: `<div class="d-flex flex-column gap-3 justify-content-center align-items-center flex-wrap w-100">
  <div class="VWOcard" style="width: 100%; maxWidth: 200px; minHeight: 120px;">
    <div class="vwo-icon-wrapper">
      <i class="icon-layout"></i>
    </div>
    <p class="text-base m-0">Candidate</p>
  </div>
  <div class="VWOcard orngBrdr" style="width: 100%; maxWidth: 200px; minHeight: 120px;">
    <div class="vwo-icon-wrapper danger">
      <i class="icon-credit-card"></i>
    </div>
    <p class="text-base m-0">Organization</p>
  </div>
</div>`,

  pricingCard: `<div class="row g-4 m-0 w-100">
  <div class="col-12 col-sm-6">
    <div class="card-pricing standard">
      <div>
        <h2 class="fw-bold">Standard</h2>
        ...
      </div>
      <button class="btn btn-outline-primary w-100 mt-4">Choose</button>
    </div>
  </div>
  <div class="col-12 col-sm-6">
    <div class="card-pricing premium">
      <div class="badge-floating">Best Value</div>
      <div>
        <h2 class="fw-bold">Premium</h2>
        ...
      </div>
      <button class="btn btn-success w-100 mt-4">Go Premium</button>
    </div>
  </div>
</div>`,

  headings: `
<h1 class="fw-bold">Page Title</h1>
<h2 class="fw-bold">Section Heading</h2>
<h3 class="fw-bold">Card Title</h3>
<h4 class="fw-bold">Sub-Label</h4>`,

  lineHeight: `
<h3 class="fw-bold mb-3 m-0">
  Enterprise Dashboard Overview<br/>
  Q3 Performance Metrics
</h3>
<p class="text-base text-muted m-0">
  This dashboard provides a comprehensive view of our quarterly performance.<br/>
  Notice how the generous line spacing makes reading multi-line data easier,<br/>
  while the tighter heading keeps the title visually grouped.
</p>
<p class="text-sm text-muted m-0">
  * Note: Small captions use a looser 1.5 line height<br/>
  to remain highly legible at tiny sizes.
</p>`,

  textTruncation: `
<p class="text-base border p-2" style="width: 200px;">
  This is a very long text string that will wrap to the next line naturally.
</p>
<p class="text-base border p-2 text-truncate" style="width: 200px;">
  This is a very long text string that will be cut off with an ellipsis because it doesn't fit.
</p>`,

  textSizes: `
<p class="text-base">Standard Body Text (Regular)</p>
<p class="text-sm">Small Caption Text (Regular)</p>`,

  colors: `<p class="text-primary text-base">Primary (Links/Actions)</p>
<p class="text-danger text-base">Danger (Errors/Deletes)</p>
<p class="text-warning text-base">Warning (Alerts/Pending)</p>
<p class="text-success text-base">Success (Validations)</p>
<p class="text-muted text-base">Muted (Hints/Metadata)</p>`,

  socialIcons: `<Icon name="Linkedin" color="#0077b5" />`,
  
  iconography: `import { IconName } from 'lucide-react';\n<IconName size={24} />`,

  spacingPadding: `
<div class="card-std" style="padding: var(--padding-card); margin-bottom: var(--margin-base);">
  <h4 class="fw-bold" style="margin-bottom: calc(var(--margin-base) * 0.5);">
    Dynamic Title
  </h4>
  <p class="text-muted" style="margin-bottom: var(--margin-base);">
    This card's padding and margins scale automatically.
  </p>
  <button class="btn btn-primary">Action</button>
</div>`,

  spacingGrid: `
<div class="bento-grid" style="gap: var(--gap-bento);">
  <div class="card-std mb-0">Grid Item 1</div>
  <div class="card-std mb-0">Grid Item 2</div>
  <div class="card-std mb-0">Grid Item 3</div>
</div>`,

  spacingForms: `
<div class="form-flex-col">
  <div>
    <label class="form-label">Email Address</label>
    <input type="email" class="form-control" />
  </div>
  <div>
    <label class="form-label">Password</label>
    <input type="password" class="form-control" />
  </div>
</div>`,

  spacingHorizontalForm: `
<div class="form-flex-col">
  <div class="row align-items-center m-0">
    <div class="col-4 p-0"><label class="form-label m-0">Company Name *</label></div>
    <div class="col-8 p-0"><input type="text" class="form-control" placeholder="Add Company Name" /></div>
  </div>
  <div class="row align-items-center m-0">
    <div class="col-4 p-0"><label class="form-label m-0">Job Title *</label></div>
    <div class="col-8 p-0"><input type="text" class="form-control" /></div>
  </div>
  <div class="row align-items-center m-0">
    <div class="col-4 p-0"><label class="form-label m-0">Department *</label></div>
    <div class="col-8 p-0"><input type="text" class="form-control" /></div>
  </div>
</div>`
};

// ==================================================================================
// 3. HELPER COMPONENTS
// ==================================================================================
const CodeBlock = ({ code }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    // Fallback copy logic to fix strict iFrame permissions
    const textArea = document.createElement("textarea");
    textArea.value = code;
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand('copy');
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy text', err);
    }
    document.body.removeChild(textArea);
  };
  return (
    <div className="card border-secondary bg-dark text-light mt-3" style={{borderRadius: 'var(--radius-md)'}}>
      <div className="card-header d-flex justify-content-between align-items-center py-2 px-3 border-secondary" style={{borderTopLeftRadius: 'var(--radius-md)', borderTopRightRadius: 'var(--radius-md)'}}>
        <small className="text-secondary font-monospace d-flex align-items-center gap-2"><Terminal size={12} /> HTML</small>
        <button onClick={handleCopy} className="btn btn-link btn-sm text-decoration-none text-light p-0 d-flex align-items-center gap-1">
          {copied ? <Check size={14} className="text-success" /> : <Copy size={14} />} {copied ? 'Copied' : 'Copy'}
        </button>
      </div>
      <pre className="card-body m-0 p-3 overflow-auto small font-monospace text-light" style={{ backgroundColor: '#1e1e1e', borderBottomLeftRadius: 'var(--radius-md)', borderBottomRightRadius: 'var(--radius-md)' }}>{code}</pre>
    </div>
  );
};

const ComponentViewer = ({ title, description, children, code, fullWidth }) => (
  <div className="card-std" id={title.replace(/\s+/g, '-').toLowerCase()}>
    <div className="mb-4">
      <h3 className="h5 fw-bold m-0 text-xl">{title}</h3>
      <p className="text-muted text-sm mt-1 mb-0">{description}</p>
    </div>
    <div className="p-4 border rounded d-flex justify-content-center align-items-center position-relative" style={{ minHeight: '160px', backgroundColor: 'var(--ui-gray-bg)' }}>
      <div className="w-100" style={{ maxWidth: fullWidth ? '100%' : '700px' }}>{children}</div>
    </div>
    <CodeBlock code={code} />
  </div>
);

// React Component specifically for the Split Dropdown Preview
const SplitButtonDemo = () => {
  const [isOpen, setIsOpen] = useState(true);
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <div className="d-flex flex-column align-items-center w-100" style={{ paddingBottom: isOpen ? '120px' : '0', transition: 'padding 0.2s ease' }}>
      <div className="add-q-split-wrapper" ref={dropdownRef}>
        <div className="add-q-split-btn">
          <button type="button" className="add-q-split-main-btn">Add/Edit Question</button>
          <button type="button" className="add-q-chevron-btn" onClick={() => setIsOpen(!isOpen)}>
            <ChevronDown size={16} />
          </button>
        </div>
        {isOpen && (
          <div className="add-q-menu">
            <button type="button" className="add-q-menu-item">
              <Edit size={16} /> Manually
            </button>
            <button type="button" className="add-q-menu-item">
              <Sparkles size={16} /> Generate with AI
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// React Component specifically for the Tag Selector Preview
const TagSelectorDemo = () => {
  const [isOpen, setIsOpen] = useState(true);
  const [availableTags, setAvailableTags] = useState([
    'In Progress', 'Interesting', 'Call to Interview', 'Not Relevant', 'Hold', 'Shortlist', 'Select', 'Hire', 'Reject'
  ]);
  const [appliedTags, setAppliedTags] = useState([]);
  const [draftTags, setDraftTags] = useState(['In Progress']);
  const [newTagInput, setNewTagInput] = useState('');
  const dropdownRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const toggleDropdown = () => {
    if (!isOpen) {
      setDraftTags([...appliedTags]);
      setNewTagInput('');
    }
    setIsOpen(!isOpen);
  };

  const handleCheckboxChange = (tag) => {
    setDraftTags((prev) => 
      prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]
    );
  };

  const handleAddTag = () => {
    const trimmedInput = newTagInput.trim();
    if (trimmedInput && !availableTags.includes(trimmedInput)) {
      setAvailableTags([...availableTags, trimmedInput]);
      setDraftTags([...draftTags, trimmedInput]);
      setNewTagInput('');
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleAddTag();
    }
  };

  const handleReset = () => setDraftTags([]);
  
  const handleApply = () => {
    setAppliedTags([...draftTags]);
    setIsOpen(false);
  };

  return (
    <div className="d-flex flex-column align-items-center w-100" style={{ paddingBottom: isOpen ? '180px' : '0', transition: 'padding 0.2s ease' }}>
      <div className="tag-selector-wrap" ref={dropdownRef}>
        <button type="button" onClick={toggleDropdown} className="btn-tag-toggle">
          <span className="text-sm text-muted">Set Tags</span>
          <ChevronDown size={16} className="text-muted" />
        </button>

        {isOpen && (
          <div className="tag-dropdown-menu">
            <div className="tag-list">
              {availableTags.map((tag, index) => (
                <div key={tag} className="d-flex justify-content-between align-items-center">
                  <label className="tag-label-item flex-grow-1 m-0 mb-1">
                    <input 
                      type="checkbox" 
                      className="custom-checkbox"
                      checked={draftTags.includes(tag)}
                      onChange={() => handleCheckboxChange(tag)}
                    />
                    <span className="tag-text">{tag}</span>
                  </label>
                  {index === 0 && (
                    <button 
                      type="button" 
                      className="text-action-link ms-4 me-2 flex-shrink-0 text-decoration-underline" 
                      style={{fontSize: 'var(--font-size-xs)', textDecoration: 'underline'}}
                      onClick={() => setDraftTags([])}
                    >
                      Clear All
                    </button>
                  )}
                </div>
              ))}
            </div>

            <div className="tag-add-section">
              <input
                type="text"
                className="tag-add-input"
                value={newTagInput}
                onChange={(e) => setNewTagInput(e.target.value)}
                onKeyDown={handleKeyDown}
                placeholder="Add Custom tag"
              />
              <button type="button" onClick={handleAddTag} className="tag-add-btn" aria-label="Add custom tag">
                +
              </button>
            </div>

            <div className="tag-footer">
              <button type="button" onClick={handleReset} className="btn-tag-reset">Reset</button>
              <button 
                type="button" 
                onClick={handleApply} 
                className="btn-tag-apply"
                disabled={draftTags.length === 0}
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ==================================================================================
// 4. SECTION COMPONENTS (MODULAR)
// ==================================================================================

const PersonalizationSection = ({ themeSettings, setThemeSettings }) => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
     <div className="bento-grid">
        {/* Color Settings */}
        <div className="card-std w-100">
           <h4 className="fw-bold mb-3">Primary Brand Color</h4>
           <div className="d-flex mb-3" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
             {['#081DA5', '#3B7CF2', '#e11d48', '#10b981', '#8b5cf6', '#14b8a6'].map(color => (
                <div 
                  key={color}
                  onClick={() => setThemeSettings(s => ({...s, primaryColor: color}))}
                  style={{
                    backgroundColor: color, 
                    width: '32px', 
                    height: '32px', 
                    borderRadius: '50%', 
                    cursor: 'pointer', 
                    border: themeSettings.primaryColor === color ? '3px solid var(--brand-text)' : '2px solid transparent'
                  }}
                />
             ))}
           </div>
           <div className="d-flex align-items-center" style={{ gap: 'var(--margin-base)' }}>
              <input 
                type="color" 
                value={themeSettings.primaryColor} 
                onChange={e => setThemeSettings(s => ({...s, primaryColor: e.target.value}))} 
                className="p-0 border-0 bg-transparent" 
                style={{width: '40px', height: '40px', cursor: 'pointer', borderRadius: '50%'}} 
              />
              <span className="text-sm text-muted font-monospace">{themeSettings.primaryColor}</span>
           </div>
        </div>

        {/* Border Radius */}
        <div className="card-std w-100">
           <h4 className="fw-bold mb-3">Border Radius (Compactness)</h4>
           <input 
             type="range" 
             min="0" max="24" 
             value={themeSettings.borderRadius}
             onChange={e => setThemeSettings(s => ({...s, borderRadius: parseInt(e.target.value)}))}
             className="custom-range w-100 mb-3"
           />
           <div className="d-flex justify-content-between text-sm text-muted">
             <span>Sharp (0px)</span>
             <span className="fw-bold text-dark">{themeSettings.borderRadius}px</span>
             <span>Rounded (24px)</span>
           </div>
        </div>

        {/* Dark Mode */}
        <div className="card-std w-100">
           <h4 className="fw-bold mb-3">Theme Mode</h4>
           <label className="custom-switch-wrap mb-2">
              <div className="d-flex w-100 justify-content-between align-items-center">
                 <span className="checkbox-label">Dark Mode</span>
                 <div className="position-relative d-flex align-items-center">
                   <input 
                     type="checkbox" 
                     className="switch-input" 
                     checked={themeSettings.isDarkMode}
                     onChange={e => setThemeSettings(s => ({...s, isDarkMode: e.target.checked}))}
                   />
                   <span className="switch-slider"></span>
                 </div>
              </div>
            </label>
            <p className="text-sm text-muted mt-2">Instantly maps all semantic CSS variables to their dark equivalents.</p>
        </div>

        {/* Layout Density */}
        <div className="card-std w-100">
           <h4 className="fw-bold mb-3">Layout Density</h4>
           <div className="d-flex flex-column" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
              <label className="custom-checkbox-wrap border rounded p-2" style={{borderColor: themeSettings.density === 'compact' ? 'var(--brand-primary)' : 'var(--ui-border)'}}>
                <input type="radio" name="density" checked={themeSettings.density === 'compact'} onChange={() => setThemeSettings(s => ({...s, density: 'compact'}))} className="custom-radio" />
                <span className="checkbox-label d-flex flex-column align-items-start">
                  <span className="fw-bold">Compact Mode</span>
                  <span className="text-sm text-muted fw-normal mt-1">Standard layout (tighter spacing & padding)</span>
                </span>
              </label>
              <label className="custom-checkbox-wrap border rounded p-2" style={{borderColor: themeSettings.density === 'comfortable' ? 'var(--brand-primary)' : 'var(--ui-border)'}}>
                <input type="radio" name="density" checked={themeSettings.density === 'comfortable'} onChange={() => setThemeSettings(s => ({...s, density: 'comfortable'}))} className="custom-radio" />
                <span className="checkbox-label d-flex flex-column align-items-start">
                  <span className="fw-bold">Comfortable Mode</span>
                  <span className="text-sm text-muted fw-normal mt-1">Breathable layout (larger margins & padding)</span>
                </span>
              </label>
           </div>
        </div>

        {/* Typography */}
        <div className="card-std w-100">
           <h4 className="fw-bold mb-3">Typography Base</h4>
           <select 
             className="form-select" 
             value={themeSettings.fontFamily}
             onChange={e => setThemeSettings(s => ({...s, fontFamily: e.target.value}))}
           >
             <option value="'Inter', sans-serif">Inter (Modern & Clean)</option>
             <option value="'Roboto', sans-serif">Roboto (Corporate Default)</option>
             <option value="'Calibri', Arial, sans-serif">Calibri (Report Default)</option>
             <option value="system-ui, -apple-system, sans-serif">System UI (Native OS Feel)</option>
           </select>
           <p className="text-sm text-muted mt-2">Allows enterprise tenants to apply their specific corporate font globally.</p>
        </div>
     </div>

     {/* Live Preview Pane */}
     <div className="card-std w-100 mt-2 p-5" style={{backgroundColor: 'var(--ui-gray-bg)'}}>
        <div className="d-flex flex-column align-items-center text-center mb-4">
            <h3 className="fw-bold mb-2">Live Component Preview</h3>
            <p className="text-muted text-sm">Notice how your changes to Color, Radius, Margin Density, and Dark Mode instantly apply to all components below.</p>
        </div>
        
        <div className="d-flex flex-wrap justify-content-center" style={{ gap: 'var(--margin-base)' }}>
            {/* Sample Button */}
            <button className="btn btn-primary shadow-sm d-flex align-items-center gap-2">
              <CheckCircle2 size={18} /> Apply Changes
            </button>
            {/* Sample Input */}
            <div className="position-relative" style={{width: '250px'}}>
               <input type="text" className="form-control" placeholder="example@email.com" />
            </div>
            {/* Sample Badge */}
            <span className="badge badge-good px-3 py-2 d-flex align-items-center gap-1">
              <Sparkles size={14} /> Premium User
            </span>
        </div>
        
        <div className="mt-4" style={{maxWidth: '500px', margin: '0 auto'}}>
           <div className="card-std mb-0 w-100">
              <h4 className="fw-bold">Settings Profile</h4>
              <p className="text-muted text-sm border-bottom pb-3 mb-3" style={{borderColor: 'var(--ui-border)'}}>Manage your application preferences here.</p>
              
              <label className="custom-checkbox-wrap mb-3">
                <input type="checkbox" className="custom-checkbox" defaultChecked />
                <span className="checkbox-label">Receive email updates</span>
              </label>
              <label className="custom-checkbox-wrap">
                <input type="checkbox" className="custom-checkbox" />
                <span className="checkbox-label">Enable two-factor authentication</span>
              </label>
           </div>
        </div>
     </div>
  </div>
);

const BestPracticesSection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
    <div className="card-std">
      <h3 className="fw-bold mb-4">Enterprise Typography & CSS Best Practices</h3>
      <p className="text-muted mb-4">
        Our style guide enforces a token-based architecture, mathematical typography scale optimized for enterprise complexity, and adaptive layouts.
      </p>

      <div className="bento-grid">
        
        {/* Token Architecture */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-primary"><Layers size={20} /></div>
            <h4 className="fw-bold m-0">1. Two-Tier Semantic Tokens</h4>
          </div>
          <p className="text-muted text-sm mb-2">Built for dynamic scalability and multi-brand support.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Primitive Tokens:</strong> Raw visual values (e.g., <code>--color-blue-500: #0d6efd</code>) defined globally.</li>
            <li><strong>Semantic Tokens:</strong> Contextual assignments (e.g., <code>--brand-primary: var(--color-blue-500)</code>) allowing seamless theme swapping without rewriting core CSS.</li>
          </ul>
        </div>

        {/* Typographic Context & Scale */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-primary"><Calculator size={20} /></div>
            <h4 className="fw-bold m-0">2. Mathematical Type Scale</h4>
          </div>
          <p className="text-muted text-sm mb-2">Limited to 4 Headings and 2 Texts for max consistency.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>11px Baseline:</strong> We use an <code>11px (0.6875rem)</code> baseline for standard body text, fit perfectly for complex enterprise data.</li>
            <li><strong>Ratio (1.125):</strong> <code>Small Text</code> and <code>H4</code> are strictly calculated using the Minor Third ratio over the base size.</li>
          </ul>
        </div>

        {/* Readability & Spacing */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-success"><Eye size={20} /></div>
            <h4 className="fw-bold m-0">3. High-Density Line Spacing</h4>
          </div>
          <p className="text-muted text-sm mb-2">Optimized for compact enterprise interfaces.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Body Text (1.4):</strong> Tighter than standard web (1.7) to fit complex data while preventing eye strain.</li>
            <li><strong>Headings (1.14):</strong> Highly compact ratio keeps large text blocks visually grouped.</li>
            <li><strong>Captions (1.5):</strong> Tiny labels require looser spacing to remain legible.</li>
            <li><strong>Truncation:</strong> Use <code>.text-truncate</code> to safely cut off overflowing text in dense tables.</li>
          </ul>
        </div>

        {/* Dynamic Spacing System */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-primary"><Ruler size={20} /></div>
            <h4 className="fw-bold m-0">4. Dynamic Spacing System (data-density)</h4>
          </div>
          <p className="text-muted text-sm mb-2">Fluid margins, paddings, and gaps.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Data Attributes:</strong> Simply apply <code>data-density="compact"</code> to the <code>&lt;html&gt;</code> or <code>&lt;body&gt;</code> tag. The CSS automatically overrides defaults globally!</li>
            <li><strong>Tokenized Spacing:</strong> Relies entirely on <code>--margin-base</code>, <code>--padding-card</code>, and <code>--gap-bento</code> to maintain a strict proportional rhythm.</li>
            <li><strong>Flex Gaps:</strong> Actively replaces hardcoded margins with CSS <code>gap</code> in flexbox/grid containers (like <code>.form-flex-col</code>) for robust alignment.</li>
          </ul>
        </div>

        {/* Adaptive Layouts */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-warning"><Layout size={20} /></div>
            <h4 className="fw-bold m-0">5. Adaptive Bento Layouts</h4>
          </div>
          <p className="text-muted text-sm mb-2">Progressive disclosure and density.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Bento Grids:</strong> CSS Grid is utilized to create modular, rectangular layouts (<code>.bento-grid</code> class).</li>
            <li><strong>Adaptive Density:</strong> Easily rearrange or prioritize content blocks dynamically to match user context.</li>
            <li><strong>Sticky Headers:</strong> Table headers stick to the top during scrolling to retain context.</li>
          </ul>
        </div>

        {/* Interaction & Glassmorphism */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-info"><Sparkles size={20} /></div>
            <h4 className="fw-bold m-0">6. Interaction & Depth</h4>
          </div>
          <p className="text-muted text-sm mb-2">Touch-safety and modern aesthetics.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Hover Safety:</strong> CSS <code>:hover</code> states are isolated within <code>@media (hover: hover)</code> to prevent "sticky" buttons on touchscreens.</li>
            <li><strong>Touch Targets:</strong> On detected touch devices, interactive elements automatically force a minimum height of <strong>44px</strong>.</li>
            <li><strong>Glassmorphism:</strong> Overlay elements utilize <code>backdrop-filter: blur()</code> to generate focus through visual depth.</li>
          </ul>
        </div>

        {/* Z-Index Tokenization */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-danger"><Layers size={20} /></div>
            <h4 className="fw-bold m-0">7. Z-Index Tokenization</h4>
          </div>
          <p className="text-muted text-sm mb-2">Predictable stacking contexts.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Centralized Tokens:</strong> Store z-indexes in `:root` (e.g., <code>--ff-z-index-app-header: 100;</code>) to prevent random "Z-Index Wars".</li>
            <li><strong>Strict Layering:</strong> Ensures modals, tooltips, and headers don't unpredictably overlap.</li>
          </ul>
        </div>

        {/* Scrollbar UI Management */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-secondary"><LayoutGrid size={20} /></div>
            <h4 className="fw-bold m-0">8. Scrollbar UI Management</h4>
          </div>
          <p className="text-muted text-sm mb-2">Clean, unobtrusive scrolling.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Slim Scrollbars:</strong> Use <code>scrollbar-width: thin;</code> and custom colors to replace clunky OS scrollbars.</li>
            <li><strong>Reduced Noise:</strong> Keeps the focus on data within dense nested tables and Kanban boards.</li>
          </ul>
        </div>

        {/* Empty & Error States */}
        <div className="p-4 border rounded h-100" style={{backgroundColor: 'var(--surface-bg)'}}>
          <div className="d-flex mb-2" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
            <div className="text-danger"><AlertTriangle size={20} /></div>
            <h4 className="fw-bold m-0">9. "Empty" & "Error" States</h4>
          </div>
          <p className="text-muted text-sm mb-2">Clear semantic UX feedback.</p>
          <ul className="text-sm text-muted ps-3 mb-0">
            <li><strong>Empty States:</strong> Use italicized, muted text (<code>.text-muted</code>) for empty tables so users know it's not broken.</li>
            <li><strong>Error States:</strong> Explicit red bottom borders (<code>.bottomBorderFieldInvalid</code>) for missing or invalid form data.</li>
          </ul>
        </div>

      </div>
    </div>
  </div>
);

const NavigationsSection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
    <div className="bento-grid">
      <ComponentViewer title="Primary Actions" description="Use for main calls-to-action." code={CODE_SNIPPETS.primaryActions}>
        <div className="d-flex flex-wrap" style={{ gap: '1rem' }}>
          <button className="btn btn-primary">Primary Action</button>
          <button className="btn btn-primary" disabled>Inactive Action</button>
          <button className="btn btn-outline-primary">Secondary Action</button>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Labeled State Toggle" description="Switch between distinct views or data states." code={CODE_SNIPPETS.labeledToggle}>
        <div className="d-flex align-items-center gap-3">
          <span className="text-sm text-muted fw-medium">Inactive Only</span>
          <label className="custom-switch-wrap m-0">
            <div className="position-relative d-flex align-items-center">
              <input type="checkbox" className="switch-input" defaultChecked />
              <span className="switch-slider"></span>
            </div>
          </label>
          <span className="text-sm text-dark fw-medium">Active Only</span>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Skip Navigation" description="Used to skip steps or move ahead quickly." code={CODE_SNIPPETS.skipLink}>
        <div className="d-flex justify-content-center">
          <button className="btn-skip">Skip</button>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Action Text Links" description="Inline hypertext links for table actions, filters, and editing." code={CODE_SNIPPETS.textLinks}>
        <div className="d-flex flex-column w-100 p-4 border rounded" style={{ gap: '1rem', backgroundColor: 'var(--surface-bg)' }}>
          <div className="d-flex justify-content-start border-bottom pb-3" style={{borderColor: 'var(--ui-border)'}}>
            <button className="text-action-link">Edit</button>
          </div>
          <div className="d-flex flex-wrap pt-1" style={{ gap: '1rem' }}>
            <button className="text-action-link">Advanced Filter</button>
            <button className="text-action-link text-decoration-underline">Clear All</button>
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Split Button Dropdown" description="Combined primary action with supplementary dropdown options." code={CODE_SNIPPETS.splitButton}>
        <SplitButtonDemo />
      </ComponentViewer>

      <ComponentViewer title="Contextual Menu" description="Compact action menu with tight spacing." code={CODE_SNIPPETS.contextMenu}>
        <div className="context-menu w-100" style={{maxWidth: '200px'}}>
          <button className="context-menu-item"><Edit size={14} /> Edit</button>
          <button className="context-menu-item"><Copy size={14} /> More Like This</button>
          <button className="context-menu-item"><Replace size={14} /> Replace</button>
          <button className="context-menu-item danger"><Trash2 size={14} /> Delete</button>
        </div>
      </ComponentViewer>
    </div>
    
    <ComponentViewer title="Horizontal Tabs" description="Top-level page switching." code={CODE_SNIPPETS.tabs} fullWidth>
      <div className="border-bottom w-100" style={{borderColor: 'var(--ui-border)'}}>
        <ul className="nav-tabs-horizontal mb-0">
          <li className="nav-item-h active">Dashboard</li>
          <li className="nav-item-h">Profile</li>
          <li className="nav-item-h">Settings</li>
        </ul>
      </div>
    </ComponentViewer>
    
    <ComponentViewer title="Table Pagination" description="Right-aligned navigation." code={CODE_SNIPPETS.pagination} fullWidth>
      <div className="w-100 p-3 border rounded" style={{backgroundColor: 'var(--surface-bg)'}}>
        <div className="pagination-wrapper" style={{ padding: '0' }}>
          <div className="pagination-nav">
            <button className="page-nav-link disabled"><ChevronsLeft size={16} /></button>
            <button className="page-nav-link disabled"><ChevronLeft size={16} /></button>
            <span className="page-number">2</span>
            <button className="page-nav-link"><ChevronRight size={16} /></button>
            <button className="page-nav-link"><ChevronsRight size={16} /></button>
          </div>
          <div className="page-select-wrapper">
            <select className="page-select" style={{ minHeight: '32px' }}><option>500</option><option>1000</option></select>
            <ChevronDown size={14} className="page-select-icon" />
          </div>
        </div>
      </div>
    </ComponentViewer>
  </div>
);

const FormsSection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>

    <ComponentViewer title="Input Fields" description="Standard form inputs maintained strictly at their enterprise 38px heights." code={CODE_SNIPPETS.inputs}>
      <div className="form-flex-col w-100">
        <div>
            <label className="form-label">Email Address</label>
            <div className="position-relative">
                <input type="email" className="form-control" placeholder="name@example.com" />
            </div>
        </div>
        <div>
            <label className="form-label">Password</label>
            <div className="position-relative">
                <input type="password" className="form-control" placeholder="••••••••" />
            </div>
        </div>
      </div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Custom Controls (Toggles & Boxes)" description="Standardized custom form controls replacing default browser styling." code={CODE_SNIPPETS.customControls}>
        <div className="d-flex flex-column w-100" style={{ gap: '1rem' }}>
          
          {/* Custom Toggle Switch */}
          <label className="custom-switch-wrap">
            <div className="d-flex w-100 justify-content-between align-items-center">
               <span className="checkbox-label">Push Notifications</span>
               <div className="position-relative d-flex align-items-center">
                 <input type="checkbox" className="switch-input" defaultChecked />
                 <span className="switch-slider"></span>
               </div>
            </div>
          </label>

          {/* Custom Checkbox */}
          <label className="custom-checkbox-wrap">
            <input type="checkbox" className="custom-checkbox" defaultChecked />
            <span className="checkbox-label">Remember this device</span>
          </label>

          {/* Custom Radio */}
          <div className="d-flex flex-wrap mt-2" style={{ gap: '1rem' }}>
            <label className="custom-checkbox-wrap">
              <input type="radio" name="demo-radio" className="custom-radio" defaultChecked />
              <span className="checkbox-label">Option A</span>
            </label>
            <label className="custom-checkbox-wrap">
              <input type="radio" name="demo-radio" className="custom-radio" />
              <span className="checkbox-label">Option B</span>
            </label>
          </div>

        </div>
      </ComponentViewer>

      <ComponentViewer title="Document Title Field" description="High-prominence input for titles." code={CODE_SNIPPETS.docTitle}>
        <div className="w-100">
          <label className="form-label text-muted text-sm uppercase">
              Document Name
          </label>
          <div className="position-relative">
               <input type="text" className="input-title-large" defaultValue="Q3 Financial Report" />
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Topic Selection Input" description="Multi-select topics with custom input." code={CODE_SNIPPETS.topicSelector}>
        <div className="w-100">
          <h4 className="fw-bold mb-1">
              1. Set Context
          </h4>
          <p className="text-sm fw-bold text-muted mb-3">
              Select main topics (max 5)
          </p>
          
          <div className="position-relative" style={{ marginBottom: '1rem' }}>
            <input type="text" className="form-control" placeholder="Add custom topic..." style={{ paddingRight: '40px' }} />
            <button 
              className="position-absolute end-0 top-50 translate-middle-y border-0 bg-transparent text-primary fw-bold d-flex align-items-center justify-content-center" 
              style={{ width: '40px', height: '100%', fontSize: '20px', cursor: 'pointer' }}
            >
              +
            </button>
          </div>
          
          <div className="d-flex flex-wrap" style={{ gap: '8px' }}>
            <div className="topic-chip">first topic</div>
            <div className="topic-chip selected">second topic</div>
            <div className="topic-chip">third topic</div>
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="OTP Input Group" description="For verification codes." code={CODE_SNIPPETS.otp}>
        <div className="d-flex flex-column align-items-center" style={{ gap: '8px' }}>
            <div className="d-flex justify-content-center" style={{ gap: '8px' }}>
              <input type="text" className="otp-input" defaultValue="4" />
              <input type="text" className="otp-input" defaultValue="2" />
              <input type="text" className="otp-input" />
              <input type="text" className="otp-input" />
            </div>
            <span className="text-muted text-sm">(Input Text)</span>
        </div>
      </ComponentViewer>
    </div>

    <ComponentViewer title="Tag Selector Dropdown" description="Custom multi-select dropdown for assigning tags, with custom tag creation." code={CODE_SNIPPETS.tagSelector} fullWidth>
      <TagSelectorDemo />
    </ComponentViewer>

    {/* RICH TEXT EDITOR MOCK */}
    <ComponentViewer title="Rich Text Editor" description="WYSIWYG editor example (Quill style)." code={CODE_SNIPPETS.rte} fullWidth>
      <div className="rich-text-editor w-100">
        <div className="rte-toolbar">
          <Bold size={16} className="rte-btn" />
          <Italic size={16} className="rte-btn" />
          <Underline size={16} className="rte-btn" />
          <span className="border-end mx-1" style={{borderColor: 'var(--ui-border)'}}></span>
          <List size={16} className="rte-btn" />
          <ListOrdered size={16} className="rte-btn" />
          <span className="border-end mx-1" style={{borderColor: 'var(--ui-border)'}}></span>
          <Link2 size={16} className="rte-btn" />
          <ImageIcon size={16} className="rte-btn" />
        </div>
        <div className="rte-content" contentEditable="true" suppressContentEditableWarning={true}>
          Start typing your content here...
        </div>
      </div>
    </ComponentViewer>
  </div>
);

const CardsSection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
    
    <ComponentViewer title="Page Container" description="Outer layout wrapper with header, soft shadow, and flexible scrolling content area. Based on the 'Group List' screen. Please note : the table content is just a place holder, this section is to show page container only " code={CODE_SNIPPETS.pageContainer} fullWidth>
      <div className="p-4 w-100" style={{backgroundColor: 'var(--ui-gray-bg)', borderRadius: '8px'}}>
        <div className="content-area" style={{height: '350px'}}>
          <div className="panel-header">
            <h3 className="fw-bold m-0">Group List</h3>
            <div className="d-flex align-items-center" style={{ gap: 'var(--margin-base)' }}>
              <div className="d-flex align-items-center gap-2">
                <span className="text-sm text-muted fw-medium">Inactive Only</span>
                <label className="custom-switch-wrap m-0">
                  <div className="position-relative d-flex align-items-center">
                    <input type="checkbox" className="switch-input" defaultChecked />
                    <span className="switch-slider"></span>
                  </div>
                </label>
                <span className="text-sm text-dark fw-medium">Active Only</span>
              </div>
              <button className="btn btn-primary">Create Assessment Link</button>
            </div>
          </div>
          <div className="assessment-table p-0">
             <table className="ant-table">
                <thead>
                  <tr>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Access Code</span>
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex flex-column align-items-center" style={{lineHeight: 0.5, opacity: 0.5}}>
                            <ArrowUp size={10} style={{marginBottom: '-1px'}}/>
                            <ArrowDown size={10}/>
                          </div>
                          <Filter size={12} className="text-muted"/>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Posted Date</span>
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex flex-column align-items-center" style={{lineHeight: 0.5, opacity: 0.5}}>
                            <ArrowUp size={10} style={{marginBottom: '-1px'}}/>
                            <ArrowDown size={10}/>
                          </div>
                          <Filter size={12} className="text-muted"/>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Form Type</span>
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex flex-column align-items-center" style={{lineHeight: 0.5, opacity: 0.5}}>
                            <ArrowUp size={10} style={{marginBottom: '-1px'}}/>
                            <ArrowDown size={10}/>
                          </div>
                          <Filter size={12} className="text-muted"/>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Company</span>
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex flex-column align-items-center" style={{lineHeight: 0.5, opacity: 0.5}}>
                            <ArrowUp size={10} style={{marginBottom: '-1px'}}/>
                            <ArrowDown size={10}/>
                          </div>
                          <Filter size={12} className="text-muted"/>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Designation</span>
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex flex-column align-items-center" style={{lineHeight: 0.5, opacity: 0.5}}>
                            <ArrowUp size={10} style={{marginBottom: '-1px'}}/>
                            <ArrowDown size={10}/>
                          </div>
                          <Filter size={12} className="text-muted"/>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Applications</span>
                        <div className="d-flex align-items-center gap-1">
                          <div className="d-flex flex-column align-items-center" style={{lineHeight: 0.5, opacity: 0.5}}>
                            <ArrowUp size={10} style={{marginBottom: '-1px'}}/>
                            <ArrowDown size={10}/>
                          </div>
                        </div>
                      </div>
                    </th>
                    <th>
                      <div className="d-flex align-items-center gap-2 text-nowrap">
                        <span>Actions</span>
                      </div>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  <tr><td>SYT0051</td><td>25-03-26 10:44 PM</td><td>Job Application</td><td>Synnat Pharma</td><td>Chemist - Production</td><td>0 / 0</td><td><button className="text-action-link"><MoreVertical size={16} className="text-muted" /></button></td></tr>
                  <tr><td>SHU0001</td><td>24-03-26 03:25 PM</td><td>Job Application</td><td>Shuban Software</td><td>Test</td><td>1 / 1</td><td><button className="text-action-link"><MoreVertical size={16} className="text-muted" /></button></td></tr>
                  <tr><td>SOL0020</td><td>20-03-26 04:21 PM</td><td>Job Application</td><td>Solon India</td><td>Sales Representatives - Field Sales</td><td>0 / 0</td><td><button className="text-action-link"><MoreVertical size={16} className="text-muted" /></button></td></tr>
                  <tr><td>SOL0019</td><td>20-03-26 12:36 PM</td><td>Job Application</td><td>Solon India</td><td>Assitant Manager - Sales Support</td><td>0 / 0</td><td><button className="text-action-link"><MoreVertical size={16} className="text-muted" /></button></td></tr>
                  <tr><td>SYT0049</td><td>18-03-26 03:08 PM</td><td>Job Application</td><td>Synnat Pharma</td><td>AR&D Manager</td><td>0 / 0</td><td><button className="text-action-link"><MoreVertical size={16} className="text-muted" /></button></td></tr>
                </tbody>
             </table>
          </div>
        </div>
      </div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Standard Card" description="Basic content container with proportional elements." code={CODE_SNIPPETS.standardCard}>
        <div className="card-std w-100">
            <h4 className="fw-bold">Card Title</h4>
            <p className="text-muted m-0">Standard card container content.</p>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Match Status Badges" description="Color-coded status indicators." code={CODE_SNIPPETS.badges}>
        <div className="d-flex flex-column w-100" style={{gap: 'var(--margin-base)', maxWidth: '500px', margin: '0 auto'}}>
          <div className="d-flex justify-content-between p-2 border-bottom" style={{borderColor: 'var(--ui-border)'}}><div className="m-0 text-base fw-bold">A</div><span className="badge badge-excellent">Excellent</span></div>
          <div className="d-flex justify-content-between p-2 border-bottom" style={{borderColor: 'var(--ui-border)'}}><div className="m-0 text-base fw-bold">B</div><span className="badge badge-good">Good</span></div>
          <div className="d-flex justify-content-between p-2 border-bottom" style={{borderColor: 'var(--ui-border)'}}><div className="m-0 text-base fw-bold">C</div><span className="badge badge-normal">Normal</span></div>
          <div className="d-flex justify-content-between p-2"><div className="m-0 text-base fw-bold">D</div><span className="badge badge-poor">Poor</span></div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Selection Cards" description="Interactive selection states." code={CODE_SNIPPETS.selectionCard}>
        <div className="d-flex flex-column w-100 m-0" style={{ gap: 'var(--margin-base)' }}>
          <div className="card-select w-100 selected position-relative h-100">
            <div className="card-select-header"><h3 className="fw-bold">Plan Pro</h3></div>
            <p className="text-center text-muted m-0">₹899/mo</p>
            <div className="position-absolute top-0 end-0 p-2 text-primary"><CheckCircle2 size={18} /></div>
          </div>
          <div className="card-select w-100 h-100">
            <div className="card-select-header"><h3 className="fw-bold">Enterprise</h3></div>
            <p className="text-center text-muted m-0">Contact Us</p>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Role Selection (VWO)" description="Icon-based selection cards." code={CODE_SNIPPETS.roleCard}>
        <div className="d-flex flex-column justify-content-center align-items-center flex-wrap w-100" style={{ gap: 'var(--margin-base)' }}>
          <div className="VWOcard" style={{width: '100%', maxWidth: '200px', minHeight: '120px'}}><div className="d-flex align-items-center justify-content-center mb-2" style={{width:40,height:40,borderRadius:'50%',background:'var(--color-info-bg)',color:'var(--brand-primary)'}}><Layout size={20}/></div><p className="text-base m-0">Candidate</p></div>
          <div className="VWOcard orngBrdr" style={{width: '100%', maxWidth: '200px', minHeight: '120px'}}><div className="d-flex align-items-center justify-content-center mb-2" style={{width:40,height:40,borderRadius:'50%',background:'var(--color-danger-bg)',color:'var(--color-danger-text)'}}><CreditCard size={20}/></div><p className="text-base m-0">Organization</p></div>
        </div>
      </ComponentViewer>
    </div>

    <ComponentViewer title="Pricing Cards" description="Tiered subscription cards." code={CODE_SNIPPETS.pricingCard} fullWidth>
      <div className="row m-0 w-100" style={{maxWidth: '800px', margin: '0 auto', gap: 'var(--gap-bento)'}}>
        <div className="col-12 col-sm-6 m-0 p-0" style={{ width: 'calc(50% - (var(--gap-bento) / 2))'}}>
          <div className="card-pricing standard">
              <div>
                  <h2 className="fw-bold">Standard</h2>
                  <ul className="small list-unstyled text-muted d-flex flex-column p-0 mt-3" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
                      <li className="d-flex gap-2"><Check size={16} className="text-primary"/>Basic</li>
                  </ul>
              </div>
              <button className="btn btn-outline-primary w-100 mt-4">Choose</button>
          </div>
        </div>
        <div className="col-12 col-sm-6 m-0 p-0" style={{ width: 'calc(50% - (var(--gap-bento) / 2))'}}>
          <div className="card-pricing premium">
              <div className="badge-floating">Best Value</div>
              <div>
                  <h2 className="fw-bold">Premium</h2>
                  <ul className="small list-unstyled text-muted d-flex flex-column p-0 mt-3" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
                      <li className="d-flex gap-2"><Check size={16} className="text-success"/>Pro</li>
                  </ul>
              </div>
              <button className="btn btn-success w-100 mt-4">Go Premium</button>
          </div>
        </div>
      </div>
    </ComponentViewer>

  </div>
);

const SpacingSection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
    
    <ComponentViewer title="Adaptive Padding & Margins" description="Cards and containers automatically adjust their internal padding and external margins based on the selected Layout Density." code={CODE_SNIPPETS.spacingPadding} fullWidth>
      <div className="w-100 p-4 d-flex justify-content-center align-items-center rounded" style={{ backgroundColor: 'var(--ui-gray-bg)' }}>
        <div style={{ backgroundColor: 'rgba(67, 117, 244, 0.2)', padding: 'var(--padding-card)', border: '1px dashed var(--brand-primary)', borderRadius: 'var(--radius-lg)' }}>
           <div style={{ backgroundColor: 'var(--surface-bg)', padding: '1rem', borderRadius: 'var(--radius-md)', textAlign: 'center', border: '1px solid var(--brand-primary)' }}>
              <span className="fw-bold text-primary">Content Area</span>
              <p className="text-sm text-muted m-0 mt-2">The blue shaded area represents <code className="text-primary">var(--padding-card)</code></p>
           </div>
        </div>
      </div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Vertical Form Spacing" description="The vertical distance between standard form groups is governed by the dynamic margin base token." code={CODE_SNIPPETS.spacingForms} fullWidth>
        <div className="w-100 p-4 d-flex justify-content-center rounded" style={{ backgroundColor: 'var(--ui-gray-bg)' }}>
          <div className="form-flex-col w-100 position-relative" style={{ maxWidth: '400px' }}>
            <div>
                <label className="form-label">Email Address</label>
                <div className="position-relative">
                    <input type="email" className="form-control" placeholder="name@example.com" />
                </div>
            </div>
            {/* Visual Overlay for Margin */}
            <div className="w-100 d-flex justify-content-center align-items-center position-absolute pointer-events-none" style={{ top: '50%', transform: 'translateY(-50%)', height: 'var(--margin-base)', backgroundColor: 'rgba(67, 117, 244, 0.15)', border: '1px dashed var(--brand-primary)', zIndex: 10 }}>
               <span className="badge bg-primary text-white" style={{ fontSize: '10px' }}>var(--margin-base)</span>
            </div>
            <div>
                <label className="form-label">Password</label>
                <div className="position-relative">
                    <input type="password" className="form-control" placeholder="••••••••" />
                </div>
            </div>
          </div>
        </div>
      </ComponentViewer>

      <ComponentViewer title="Horizontal Form Spacing" description="Prevents forms from 'spreading out' by stripping row margins and wrapping them in a flex container with a controlled gap token." code={CODE_SNIPPETS.spacingHorizontalForm} fullWidth>
        <div className="w-100 p-4 rounded" style={{ backgroundColor: 'var(--ui-gray-bg)' }}>
          <div className="card-std w-100 p-4 mb-0">
            <div className="form-flex-col">
              
              <div className="row align-items-center m-0 position-relative">
                <div className="col-4 p-0"><label className="form-label text-sm fw-bold text-muted m-0">Company Name *</label></div>
                <div className="col-8 p-0"><input type="text" className="form-control" placeholder="Add Company Name" /></div>
              </div>
              
              <div className="row align-items-center m-0 position-relative">
                <div className="col-4 p-0"><label className="form-label text-sm fw-bold text-muted m-0">Job Title *</label></div>
                <div className="col-8 p-0"><input type="text" className="form-control" /></div>
                {/* Visual Overlay for Margin */}
                <div className="w-100 d-flex justify-content-center align-items-center position-absolute pointer-events-none" style={{ top: 0, transform: 'translateY(-100%)', height: 'var(--margin-base)', backgroundColor: 'rgba(67, 117, 244, 0.15)', border: '1px dashed var(--brand-primary)', zIndex: 10 }}>
                   <span className="badge bg-primary text-white" style={{ fontSize: '10px' }}>var(--margin-base)</span>
                </div>
              </div>

              <div className="row align-items-center m-0">
                <div className="col-4 p-0"><label className="form-label text-sm fw-bold text-muted m-0">No. Of Positions</label></div>
                <div className="col-8 p-0"><input type="text" className="form-control" /></div>
              </div>

            </div>
          </div>
        </div>
      </ComponentViewer>
    </div>

    <ComponentViewer title="Bento Grid Gaps" description="Grid layouts utilize the dynamic gap property, scaling down for compact dashboard views and expanding for comfortable reading." code={CODE_SNIPPETS.spacingGrid} fullWidth>
      <div className="w-100 p-4 rounded" style={{ backgroundColor: 'var(--ui-gray-bg)' }}>
         <div className="bento-grid w-100" style={{ backgroundColor: 'rgba(16, 185, 129, 0.2)', padding: 'var(--gap-bento)', border: '1px dashed var(--color-success-text)', borderRadius: 'var(--radius-lg)' }}>
            <div className="card-std mb-0 d-flex align-items-center justify-content-center" style={{ minHeight: '100px', border: '1px solid var(--color-success-text)' }}>
              <span className="fw-bold text-success">Grid Item 1</span>
            </div>
            <div className="card-std mb-0 d-flex align-items-center justify-content-center" style={{ minHeight: '100px', border: '1px solid var(--color-success-text)' }}>
              <span className="fw-bold text-success">Grid Item 2</span>
            </div>
         </div>
         <p className="text-sm text-center mt-3 text-muted">The green shaded area represents <code className="text-success">var(--gap-bento)</code></p>
      </div>
    </ComponentViewer>

  </div>
);

const TypographySection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
    
    <ComponentViewer title="Heading Hierarchy" description="Semantic headings." code={CODE_SNIPPETS.headings} fullWidth>
      <div className="d-flex flex-column w-100" style={{gap: 'calc(var(--margin-base) * 0.5)', maxWidth: '800px', margin: '0 auto'}}>
        <div className="d-flex justify-content-between align-items-center">
          <h1 className="fw-bold m-0">Page Title (H1)</h1>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h2 className="fw-bold m-0">Section Heading (H2)</h2>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h3 className="fw-bold m-0">Card Title (H3)</h3>
        </div>
        <div className="d-flex justify-content-between align-items-center mt-2">
          <h4 className="fw-bold m-0">Sub-Label (H4)</h4>
        </div>
      </div>
    </ComponentViewer>

    <ComponentViewer title="Line Spacing (Leading)" description="Comparing tighter headings (1.14), readable body text (1.4), and looser captions (1.5)." code={CODE_SNIPPETS.lineHeight} fullWidth>
      <div className="d-flex flex-column w-100" style={{gap: 'var(--margin-base)', maxWidth: '800px', margin: '0 auto'}}>
        <div className="p-4 border rounded position-relative" style={{background: 'var(--surface-bg)'}}>
          <span className="badge badge-good position-absolute top-0 end-0 m-3">Line-height: 1.14</span>
          <h3 className="fw-bold m-0 demo-highlight-primary">
            Enterprise Dashboard Overview<br/>
            Q3 Performance Metrics
          </h3>
        </div>
        <div className="p-4 border rounded position-relative" style={{background: 'var(--surface-bg)'}}>
          <span className="badge badge-normal position-absolute top-0 end-0 m-3">Line-height: 1.4</span>
          <p className="text-base text-muted m-0 demo-highlight-success">
            This dashboard provides a comprehensive view of our quarterly performance.<br/>
            Notice how the optimized line spacing makes reading multi-line data easier,<br/>
            while the tighter heading keeps the title visually grouped.
          </p>
        </div>
        <div className="p-4 border rounded position-relative" style={{background: 'var(--surface-bg)'}}>
          <span className="badge badge-poor position-absolute top-0 end-0 m-3">Line-height: 1.5</span>
          <p className="text-sm text-muted m-0 demo-highlight-danger">
            * Note: Small captions and footnotes use a looser 1.5 line height<br/>
            to ensure that tiny characters remain highly legible.
          </p>
        </div>
      </div>
    </ComponentViewer>

    <div className="bento-grid">
      <ComponentViewer title="Text Truncation" description="Handling long text in dense layouts." code={CODE_SNIPPETS.textTruncation}>
        <div className="d-flex flex-column w-100" style={{ gap: 'var(--margin-base)' }}>
          <div>
             <div className="text-sm fw-bold mb-1 text-muted">Standard Wrapping</div>
             <p className="text-base border p-2 rounded" style={{width: '200px', background: 'var(--surface-bg)'}}>
               This is a very long text string that will wrap to the next line naturally.
             </p>
          </div>
          <div>
             <div className="text-sm fw-bold mb-1 text-muted">Truncated (.text-truncate)</div>
             <p className="text-base border p-2 rounded text-truncate" style={{width: '200px', background: 'var(--surface-bg)'}}>
               This is a very long text string that will be cut off with an ellipsis because it doesn't fit.
             </p>
          </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Text Sizes" description="Standard sizes." code={CODE_SNIPPETS.textSizes}>
        <div className="d-flex flex-column w-100" style={{ gap: 'var(--margin-base)' }}>
           <div>
              <p className="text-muted text-sm mb-1">Standard Body Text (11px / 0.6875rem)</p>
              <div className="d-flex flex-column" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
                 <p className="text-base fw-normal m-0">Normal Weight (400)</p>
                 <p className="text-base fw-bold m-0">Bold Weight (700)</p>
              </div>
           </div>
           <hr className="border-light m-0" style={{borderColor: 'var(--ui-border)'}} />
           <div>
              <p className="text-muted text-sm mb-1">Small Text (~9.77px / Base &divide; Ratio)</p>
              <div className="d-flex flex-column" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
                 <p className="text-sm fw-normal m-0">Normal Weight (400)</p>
                 <p className="text-sm fw-bold m-0">Bold Weight (700)</p>
              </div>
           </div>
        </div>
      </ComponentViewer>
      
      <ComponentViewer title="Colors & Usage" description="Contextual usage. All shown in Base size (11px)." code={CODE_SNIPPETS.colors}>
        <div className="d-flex flex-column w-100" style={{ gap: 'calc(var(--margin-base) * 0.5)' }}>
          <p className="text-primary text-base m-0">Primary (Links/Actions)</p>
          <p className="text-danger text-base m-0">Danger (Errors/Deletes)</p>
          <p className="text-warning text-base m-0">Warning (Alerts/Pending)</p>
          <p className="text-success text-base m-0">Success (Validations)</p>
          <p className="text-muted text-base m-0">Muted (Hints/Metadata)</p>
        </div>
      </ComponentViewer>
    </div>
  </div>
);

// ----------------------------------------------------------------------------------
// 5. ICON DATA & SECTION
// ----------------------------------------------------------------------------------

const ICON_MAP = {
  Menu, X, Download, Copy, Check, Type, MousePointer2, Layout, 
  CreditCard, MessageSquare, ChevronRight, Terminal, CheckCircle2, 
  Star, Briefcase, MapPin, Smile, User, Settings, Calendar, Edit, 
  AlertCircle, HelpCircle, Info, ArrowLeft, Building2, BadgeCheck, 
  CloudUpload, Mail, Zap, FileText, CheckCircle, Loader2, Sparkles, 
  Clock, Share2, UserPlus, Link, Linkedin, ThumbsUp, Lock, IndianRupee, 
  Twitter, Github, Facebook, Instagram, FileUser, Scan, Users, BookOpen, 
  Brain, CircleX, AlertTriangle, List, LogOut, Target, MoreVertical, Home,
  ChevronLeft, ChevronsLeft, ChevronsRight, ChevronDown, LayoutGrid,
  RefreshCw, Replace, MoreHorizontal, Filter, ArrowUp, ArrowDown, Plus, Trash2,
  BookMarked, Monitor, Smartphone, Ruler, Eye, Calculator, Layers, Palette
};

export const Icon = ({ name, size = 24, className = '', color, ...props }) => {
  const LucideIcon = ICON_MAP[name];
  if (!LucideIcon) return null;
  return <LucideIcon size={size} className={className} color={color} {...props} />;
};

const commonIcons = [
    { name: 'Grid View', icon: 'LayoutGrid' },
    { name: 'List View', icon: 'List' },
    { name: 'Regenerate', icon: 'RefreshCw' },
    { name: 'Replace', icon: 'Replace' },
    { name: 'More', icon: 'MoreHorizontal' },
    { name: 'ArrowLeft', icon: 'ArrowLeft' },
    { name: 'Briefcase', icon: 'Briefcase' },
    { name: 'Building2', icon: 'Building2' },
    { name: 'BadgeCheck', icon: 'BadgeCheck' },
    { name: 'UploadCloud', icon: 'CloudUpload' },
    { name: 'ChevronRight', icon: 'ChevronRight' },
    { name: 'User', icon: 'User' },
    { name: 'Mail', icon: 'Mail' },
    { name: 'MapPin', icon: 'MapPin' },
    { name: 'Calendar', icon: 'Calendar' },
    { name: 'Zap', icon: 'Zap' },
    { name: 'FileText', icon: 'FileText' },
    { name: 'CheckCircle', icon: 'CheckCircle' },
    { name: 'Loader2', icon: 'Loader2' },
    { name: 'Check', icon: 'Check' },
    { name: 'Sparkles', icon: 'Sparkles' },
    { name: 'X', icon: 'X' },
    { name: 'Info', icon: 'Info' },
    { name: 'Clock', icon: 'Clock' },
    { name: 'Download', icon: 'Download' },
    { name: 'Share2', icon: 'Share2' },
    { name: 'UserPlus', icon: 'UserPlus' },
    { name: 'Edit', icon: 'Edit' },
    { name: 'Link', icon: 'Link' },
    { name: 'WhatsApp', icon: 'MessageSquare' },
    { name: 'ThumbsUp', icon: 'ThumbsUp' },
    { name: 'Money', icon: 'IndianRupee' },
    { name: 'Lock', icon: 'Lock' },
    { name: 'Star', icon: 'Star' },
    { name: 'Scan', icon: 'Scan' },
    { name: 'FileUser', icon: 'FileUser' },
    { name: 'Users', icon: 'Users' },
    { name: 'Book', icon: 'BookOpen' },
    { name: 'Brain', icon: 'Brain' },
    { name: 'XCircle', icon: 'CircleX' },
    { name: 'Alert', icon: 'AlertTriangle' },
    { name: 'List', icon: 'List' },
    { name: 'LogOut', icon: 'LogOut' },
    { name: 'Target', icon: 'Target' },
    { name: 'MoreVertical', icon: 'MoreVertical' },
    { name: 'Home', icon: 'Home' },
    { name: 'Plus', icon: 'Plus' },
    { name: 'Monitor', icon: 'Monitor' },
    { name: 'Smartphone', icon: 'Smartphone' },
    { name: 'Ruler', icon: 'Ruler' },
];

const socialIcons = [
    { name: 'Linkedin', icon: 'Linkedin', color: '#0077b5' },
    { name: 'Github', icon: 'Github', color: '#333' },
    { name: 'Facebook', icon: 'Facebook', color: '#1877f2' },
    { name: 'Twitter', icon: 'Twitter', color: '#1da1f2' },
    { name: 'Instagram', icon: 'Instagram', color: '#e1306c' },
];

const IconsSection = () => (
  <div className="d-flex flex-column" style={{ gap: 'var(--gap-bento)' }}>
    <ComponentViewer title="Social Media Brand Icons" description="Official brand color icons." code={CODE_SNIPPETS.socialIcons} fullWidth>
      <div className="row g-3 w-100 m-0">
        {socialIcons.map((item) => (
          <div key={item.name} className="col-6 col-sm-4 col-md-3 col-lg-2">
            <div className="d-flex flex-column align-items-center justify-content-center p-3 border rounded h-100 w-100" style={{backgroundColor: 'var(--surface-bg)'}}>
                <div className="mb-2"><Icon name={item.icon} color={item.color} /></div>
                <div className="text-sm text-muted font-monospace">{item.name}</div>
            </div>
          </div>
        ))}
      </div>
    </ComponentViewer>
    <ComponentViewer title="Iconography" description="Lucide React icons." code={CODE_SNIPPETS.iconography} fullWidth>
      <div className="row g-3 w-100 m-0">
        {commonIcons.map((item) => (
          <div key={item.name} className="col-6 col-sm-4 col-md-3 col-lg-2">
            <div className="d-flex flex-column align-items-center justify-content-center p-3 border rounded h-100 w-100" style={{backgroundColor: 'var(--surface-bg)'}}>
                <div className="mb-2 text-secondary"><Icon name={item.icon} /></div>
                <div className="text-sm text-muted font-monospace">{item.name}</div>
            </div>
          </div>
        ))}
      </div>
    </ComponentViewer>
  </div>
);

// ==================================================================================
// 6. MAIN APP WRAPPER
// ==================================================================================
export default function App() {
  const [activeTab, setActiveTab] = useState('navigations');
  
  // State for Live Personalization Engine
  const [themeSettings, setThemeSettings] = useState({
    primaryColor: '#081DA5', // Default set to exact brand logo color
    borderRadius: 8,
    isDarkMode: false,
    density: 'compact',
    fontFamily: "'Inter', sans-serif"
  });

  // Inject CSS Variables on the fly!
  useEffect(() => {
    const root = document.documentElement;
    
    // Natively assign the density layout directly to the HTML root
    root.setAttribute('data-density', themeSettings.density);
    
    root.style.setProperty('--brand-primary', themeSettings.primaryColor);
    root.style.setProperty('--radius-md', `${themeSettings.borderRadius}px`);
    root.style.setProperty('--radius-lg', `${themeSettings.borderRadius * 1.5}px`);
    root.style.setProperty('--font-family-base', themeSettings.fontFamily);

    if (themeSettings.isDarkMode) {
      // Dark Mode logic strictly aligned to brand colors
      root.style.setProperty('--brand-bg', '#121418'); // Deeper shade of #1F222A 
      root.style.setProperty('--brand-text', '#F3F5F9'); // Off-White
      root.style.setProperty('--surface-bg', '#1F222A'); // Primary Dark Mode background
      root.style.setProperty('--surface-bg-hover', '#2b2f3a');
      root.style.setProperty('--surface-bg-alpha', 'rgba(31, 34, 42, 0.85)');
      root.style.setProperty('--ui-gray-bg', '#2b2f3a'); 
      root.style.setProperty('--ui-border', '#3f3f46'); 
      root.style.setProperty('--ui-text-muted', '#a1a1aa'); 
      root.style.setProperty('--ui-text-dark', '#e4e4e7'); 
      
      // Adapted dark mode specific semantic colors to prevent clashes
      root.style.setProperty('--color-info-bg', 'rgba(59, 124, 242, 0.15)'); 
      root.style.setProperty('--color-success-bg', 'rgba(16, 185, 129, 0.1)'); 
      root.style.setProperty('--color-warning-bg', 'rgba(245, 158, 11, 0.1)');
      root.style.setProperty('--color-danger-bg', 'rgba(239, 68, 68, 0.1)');
      root.style.setProperty('--color-info-text', '#93c5fd'); 
      root.style.setProperty('--color-success-text', '#34d399'); 
      root.style.setProperty('--color-warning-text', '#fde047');
      root.style.setProperty('--color-danger-text', '#fca5a5');
    } else {
      root.style.removeProperty('--brand-bg'); 
      root.style.removeProperty('--brand-text');
      root.style.removeProperty('--surface-bg');
      root.style.removeProperty('--surface-bg-hover');
      root.style.removeProperty('--surface-bg-alpha');
      root.style.removeProperty('--ui-gray-bg');
      root.style.removeProperty('--ui-border');
      root.style.removeProperty('--ui-text-muted');
      root.style.removeProperty('--ui-text-dark');
      root.style.removeProperty('--color-info-bg');
      root.style.removeProperty('--color-success-bg');
      root.style.removeProperty('--color-warning-bg');
      root.style.removeProperty('--color-danger-bg');
      root.style.removeProperty('--color-info-text');
      root.style.removeProperty('--color-success-text');
      root.style.removeProperty('--color-warning-text');
      root.style.removeProperty('--color-danger-text');
    }
  }, [themeSettings]);

  useEffect(() => { document.title = "Lywo Style Guide"; }, []);

  const handleDownload = () => {
    const blob = new Blob([MERGED_STYLES], { type: 'text/css' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = 'main.css';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const navItems = [
    { id: 'personalization', label: 'Personalization', icon: <Palette size={18} /> },
    { id: 'practices', label: 'Best Practices', icon: <BookMarked size={18} /> },
    { id: 'spacing', label: 'Dynamic Spacing', icon: <Ruler size={18} /> },
    { id: 'typography', label: 'Typography', icon: <Type size={18} /> },
    { id: 'navigations', label: 'Navigations', icon: <MousePointer2 size={18} /> },
    { id: 'forms', label: 'Forms & Inputs', icon: <Layout size={18} /> },
    { id: 'cards', label: 'Cards & Containers', icon: <CreditCard size={18} /> },
    { id: 'icons', label: 'Icons', icon: <Smile size={18} /> },
  ];

  return (
    <div className="ds-wrapper desktop-preview">
      <style>{MERGED_STYLES}</style>
      <header className="ds-header">
        <div className="ds-brand-row">
          <div className="d-flex align-items-center gap-2 text-primary fw-bold text-xl">
            <Layout size={24} />
            <span>Lywo Style Guide</span>
          </div>
          
          <div className="d-flex align-items-center gap-3">
             <button onClick={handleDownload} className="btn btn-outline-primary btn-sm d-flex align-items-center gap-2">
               <Download size={14} /> Download CSS
             </button>
          </div>
        </div>
        <div className="ds-nav-row">
          <ul className="nav-tabs-horizontal">
            {navItems.map((item) => (
              <li key={item.id} className={`nav-item-h ${activeTab === item.id ? 'active' : ''}`} onClick={() => setActiveTab(item.id)}>
                {item.icon} {item.label}
              </li>
            ))}
          </ul>
        </div>
      </header>
      <main className="ds-content">
        <header className="mb-5">
          <h1 className="fw-bold text-capitalize mb-2">{activeTab === 'personalization' ? 'Theme Personalization' : activeTab}</h1>
          <p className="text-muted text-base">Guidelines, classes, and live previews for {activeTab}.</p>
        </header>
        {activeTab === 'personalization' && <PersonalizationSection themeSettings={themeSettings} setThemeSettings={setThemeSettings} />}
        {activeTab === 'practices' && <BestPracticesSection />}
        {activeTab === 'spacing' && <SpacingSection />}
        {activeTab === 'navigations' && <NavigationsSection />}
        {activeTab === 'forms' && <FormsSection />}
        {activeTab === 'cards' && <CardsSection />}
        {activeTab === 'typography' && <TypographySection />}
        {activeTab === 'icons' && <IconsSection />}
      </main>
    </div>
  );
}

